import "./PermanentMap.css";

import { Controller } from "@hotwired/stimulus";
import { preferredLanguagesChangedEvent } from "../Localization/PreferredLanguageNavigation";
import {
    boundsAround,
    destinationBoundingBoxToLngLatBounds,
    hasUsableBoundingBox,
    hasUsableCoordinates,
    hasUsableViewportBounds,
    viewportBoundsToBboxes
} from "./SmartGuideMapViewport";
import { DomEvents } from "../Generated/DomEvents";
import type { PersistentAudioMedia } from "../PersistentAudioPlayer/PersistentAudioPlayerUi";
import { tourPoiSelectionChangedEventName, type TourPoiSelectionSnapshot } from "../TourTray/TourPoiSelectionEvents";
import { getTourPoiSelectionState } from "../TourTray/TourPoiSelectionState";
import { getTourPoiCameraBounds, getTourPoiCameraPadding, tourPoiCameraDurationMs, tourPoiCameraMaxZoom, type TourPoiCameraLayout } from "./TourPoiCamera";
import { closePermanentMapEventName } from "./SmartGuidePermanentMapEvents";
import { openHomeSearchEventName, returnToHomeMapEventName } from "../../Home/HomeEvents";
import type { SmartGuideBrowserLocation } from "../BrowserLocation/SmartGuideBrowserLocation";
import { smartGuideLocationBroker } from "../BrowserLocation/SmartGuideLocationBroker";
import type {
    DestinationMapBoundingBox,
    DestinationTourRoute,
    DestinationRoutePoiMarker,
    SmartGuideMapItem,
    SmartGuideMapItemsResponse,
    SmartGuidePermanentMapContext,
    ViewportBounds
} from "./SmartGuidePermanentMapTypes";
import { formatTranslation, smartGuideFrontendSettings, uiLanguage, webTranslations } from "../webTranslations";

const sourceId = "smartguide-permanent-map-items";
const routeSourceId = "smartguide-permanent-map-route";
const routeLayerId = "smartguide-permanent-map-route";
const routeHitLayerId = "smartguide-permanent-map-route-hit";
const routeLineWidth = 2.5;
const routeHitLineWidth = 32;
const markerLayerId = "smartguide-permanent-map-items-large";
const practicalMarkerLayerId = "smartguide-permanent-map-items-practical";
const selectedMarkerLayerId = "smartguide-permanent-map-items-selected";
const nativeMarkerIconSize = 0.5;
const practicalMarkerSubcategories = new Set([
    "airport", "atm", "beach", "bicycle_parking", "bicycle_rental", "bicycle_service",
    "bus_station", "car_rental", "currency_exchange", "drinking_water", "embassy", "ferry",
    "gas_station", "gym", "hospital", "laundry", "lift", "parking", "pharmacy", "photo_point",
    "picnic_site", "playground", "police", "post_office", "public_toilets", "subway_station",
    "swimming", "taxi", "tourist_information", "train_station", "tram_station", "trolleybus_station", "viewpoint"
]);
const originalSmartGuideImagePrefix = "https://d37rmf1ynyg9aw.cloudfront.net/fit-in/700x700/data/v4/";
const generatedMapIconBaseUrl = "https://du8wuy497pbt7.cloudfront.net";
const userLocationSourceId = "smartguide-permanent-map-user-location";
const userLocationAccuracyLayerId = "smartguide-permanent-map-user-location-accuracy";
const userLocationBlue = "rgb(29, 145, 252)";
const tourMarkerBlue = "rgb(6, 97, 224)";
const selectedPoiRed = "rgb(231, 4, 77)";
const userLocationIpFallbackUrl = "https://api.bigdatacloud.net/data/reverse-geocode-client";
const userLocationIpFallbackTimeoutMs = 3000;
const homeDefaultZoom = 9;
const homeDefaultBounds: ViewportBounds = {
    west: -180,
    south: -60,
    east: 180,
    north: 85
};
const geolocationPermissionDeniedCode = 1;
const historyStateMapVisibleKey = "smartGuidePermanentMapVisible";
const historyStatePageScrollKey = "smartGuidePermanentMapPageScroll";
const historyStateSelectedMapItemCameraKey = "smartGuidePermanentMapSelectedMapItemCamera";
const historyEntryMapVisibleStoragePrefix = "smartguide:permanent-map-visible:";

type UserLocationHeadingSource = "compass" | "geolocation";
type UserLocationSource = "gps" | "ip";

type DeviceOrientationEventWithWebkitCompass = DeviceOrientationEvent & {
    webkitCompassHeading?: number;
};

type IpLocationResponse = {
    latitude?: number;
    longitude?: number;
};

type HomeContinentMapEventDetail = {
    code?: string;
    label?: string;
    language?: string;
    bounds?: ViewportBounds;
};

type DestinationNavigationResponse = {
    url?: string | null;
};

type NativeMarkerIcon = {
    id: string;
    url?: string;
    image?: ImageData | HTMLImageElement | ImageBitmap;
};

type PermanentMapElementState = {
    tourPoiCameraLayout?: TourPoiCameraLayout;
    appliedTourSelectionRevision?: number;
    map?: any;
    mapModule?: any;
    selectedItem?: SmartGuideMapItem;
    selectedTourRoute?: DestinationTourRoute;
    styleLoaded: boolean;
    lastItems?: SmartGuideMapItem[];
    appliedContextKey?: string;
    pageScrollBeforeMap?: { left: number; top: number };
    localDestinationItemCaches?: Map<string, SmartGuideMapItem[]>;
    currentLocationPointerElement?: HTMLElement;
    userLocationMarker?: any;
    userLocationMarkerElement?: HTMLElement;
    userLocationHeading?: number;
    userLocationHeadingSource?: UserLocationHeadingSource;
    userLocationSource?: UserLocationSource;
    userLocationLngLat?: [number, number];
    userLocationAccuracy?: number;
    followUserLocation?: boolean;
    photoMarkerDomMarkers?: Map<string, PhotoMarkerDomMarker>;
    restoreSelectedTourPoiCameraOnNextTourShow?: boolean;
};

type MapCamera = {
    center: [number, number];
    zoom: number;
    bearing: number;
    pitch: number;
    padding: { top: number; right: number; bottom: number; left: number };
};

type SelectedMapItemCamera = MapCamera & {
    mapPath: string;
    selectedItem: SmartGuideMapItem;
    viewport?: string;
};

type HomeMapSearchState = {
    context: SmartGuidePermanentMapContext;
    camera?: MapCamera;
    viewport?: string;
};

type PhotoMarkerDomMarker = {
    marker: any;
    iconUrl: string;
};

const permanentMapElementStates = new WeakMap<HTMLElement, PermanentMapElementState>();

export default class PermanentMapController extends Controller<HTMLElement> {
    static values = {
        itemsUrl: String,
        styleUrl: String
    };

    declare readonly itemsUrlValue: string;
    declare readonly styleUrlValue: string;

    private currentContext?: SmartGuidePermanentMapContext;
    private viewportAbortController?: AbortController;
    private viewportRequestKey?: string;
    private viewportReloadRequired = false;
    private mapHandlersBound = false;
    private suppressedSyntheticClick?: { key: string; expiresAt: number };
    private unsubscribeMapLocation?: () => void;
    private shouldCenterOnNextUserLocation = false;
    private centerOnNextUserLocationZoom?: number;
    private compassPermissionRequestInFlight = false;
    private compassPermissionGranted = false;
    private compassHeadingListening = false;
    private locationStatusMessage?: string;
    private ipLocationFallbackPromise?: Promise<void>;
    private loadedMarkerImageIds = new Set<string>();
    private loadingMarkerImageIds = new Set<string>();
    private placeholderMarkerIcons = new Map<string, NativeMarkerIcon>();
    private renderedMarkerIds = new Set<string>();
    private suppressedContextSelectedPoiId?: string;
    private renderedMarkerRefreshQueued = false;
    private renderedMarkerIdleRefreshQueued = false;
    private renderedMarkerRefreshTimeoutId?: number;
    private destinationNavigationAbortController?: AbortController;
    private cameraLayoutObserver?: ResizeObserver;
    private cameraLayoutElements = new Set<HTMLElement>();
    private cameraLayoutFrame?: number;

    connect(): void {
        const preserveStateDuringNavigation =
            this.shouldPreserveStateDuringNavigation() ||
            this.element.dataset.smartGuidePermanentMapExploreAfterNavigation === "true";
        if (!preserveStateDuringNavigation) {
            this.refreshContext();
        }
        this.bindMapHandlers();
        this.renderCurrentLocationFollowState();
        if (!this.element.hidden && !preserveStateDuringNavigation) {
            document.body.classList.add("smartguide-map-visible");
            this.setMapNavActive(true);
            this.applyContextCameraIfNeeded();
            this.requestCurrentLocation(false, { requestCompass: false });
        }
        document.addEventListener("pointerup", this.onDocumentPointerUp, true);
        document.addEventListener("click", this.onDocumentClick, true);
        document.addEventListener(closePermanentMapEventName, this.onClosePermanentMap);
        document.addEventListener(returnToHomeMapEventName, this.onReturnToHomeMap);
        document.addEventListener("smartguide:open-continent-map", this.onOpenContinentMap as EventListener);
        document.addEventListener(tourPoiSelectionChangedEventName, this.onTourPoiSelectionChanged);
        document.addEventListener("turbo:load", this.onTurboLoad);
        document.addEventListener("turbo:render", this.onTurboRender);
        document.addEventListener("turbo:before-render", this.onBeforeRender);
        document.addEventListener("turbo:before-cache", this.onBeforeCache);
        window.addEventListener("pageshow", this.onPageShow);
        window.addEventListener(preferredLanguagesChangedEvent, this.onPreferredLanguagesChanged);
        this.observeCameraLayout();
        if (this.element.hidden &&
            this.isFullDocumentHistoryRestore() &&
            this.historyStateMapVisible() === true) {
            void this.show({ restoreScrollFromHistory: true });
        }
    }

    disconnect(): void {
        this.cameraLayoutObserver?.disconnect();
        this.cameraLayoutObserver = undefined;
        this.cameraLayoutElements.clear();
        if (this.cameraLayoutFrame !== undefined) window.cancelAnimationFrame(this.cameraLayoutFrame);
        this.cameraLayoutFrame = undefined;
        document.removeEventListener("pointerup", this.onDocumentPointerUp, true);
        document.removeEventListener("click", this.onDocumentClick, true);
        document.removeEventListener(closePermanentMapEventName, this.onClosePermanentMap);
        document.removeEventListener(returnToHomeMapEventName, this.onReturnToHomeMap);
        document.removeEventListener("smartguide:open-continent-map", this.onOpenContinentMap as EventListener);
        document.removeEventListener(tourPoiSelectionChangedEventName, this.onTourPoiSelectionChanged);
        document.removeEventListener("turbo:load", this.onTurboLoad);
        document.removeEventListener("turbo:render", this.onTurboRender);
        document.removeEventListener("turbo:before-render", this.onBeforeRender);
        document.removeEventListener("turbo:before-cache", this.onBeforeCache);
        window.removeEventListener("pageshow", this.onPageShow);
        window.removeEventListener(preferredLanguagesChangedEvent, this.onPreferredLanguagesChanged);
        this.unbindMapHandlers();
        this.destinationNavigationAbortController?.abort();
        this.destinationNavigationAbortController = undefined;
        this.viewportAbortController?.abort();
        this.clearRenderedMarkerRefreshTimeout();
        this.clearRenderedMarkerIdleRefresh();
        this.stopGeolocationWatch();
        this.stopCompassHeadingUpdates();
    }

    private readonly onPreferredLanguagesChanged = (): void => {
        this.viewportAbortController?.abort();
        this.viewportRequestKey = undefined;
        this.viewportReloadRequired = true;
        void this.loadViewportItems();
    };

    private readonly onDocumentClick = (event: MouseEvent): void => {
        const target = event.target instanceof Element ? event.target : null;
        const searchButton = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-search]");
        if (searchButton && this.element.contains(searchButton)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.openSearchFromMap();
            return;
        }

        const filterButton = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-filter-labels], [data-smart-guide-permanent-map-filter-categories]");
        if (filterButton && this.element.contains(filterButton)) {
            event.preventDefault();
            filterButton.setAttribute("aria-pressed", filterButton.getAttribute("aria-pressed") === "true" ? "false" : "true");
            void this.loadViewportItems();
            return;
        }

        const currentLocationButton = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-current-location]");
        if (currentLocationButton && this.element.contains(currentLocationButton)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.centerOnCurrentLocationWhenAvailable();
            return;
        }

        const compassButton = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-compass]");
        if (compassButton && this.element.contains(compassButton)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.resetMapBearing();
            return;
        }

        const currentLocationPointer = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-current-location-pointer]");
        if (currentLocationPointer && this.element.contains(currentLocationPointer)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.centerOnKnownUserLocation();
            return;
        }

        const markerAudioButton = target?.closest<HTMLElement>("[data-smart-guide-permanent-map-marker-audio]");
        if (markerAudioButton && this.element.contains(markerAudioButton)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.playSelectedMarkerAudio();
            return;
        }

        const markerDetailLink = target?.closest<HTMLAnchorElement>("[data-smart-guide-permanent-map-marker-detail]");
        if (markerDetailLink && this.element.contains(markerDetailLink) && this.isVisible) {
            if (!this.markerDetailLinkCanNavigate(markerDetailLink)) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            if (this.markerDetailLinkTargetsCurrentPoi()) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.hide();
                return;
            }

            const homeDestinationNavigationUrl = this.homeDestinationNavigationUrl(event, markerDetailLink);
            if (homeDestinationNavigationUrl) {
                event.preventDefault();
                event.stopImmediatePropagation();
                void this.navigateToHomeDestination(markerDetailLink, homeDestinationNavigationUrl);
                return;
            }

            this.prepareExploreModeAfterMarkerDetailNavigation(event, markerDetailLink);
            return;
        }

        const key = this.bottomNavigationKeyFromEvent(event);
        if (key && this.shouldSuppressSyntheticClick(key)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        this.handleBottomNavigationEvent(event);
    };

    private readonly onClosePermanentMap = (): void => {
        if (!this.isVisible) {
            return;
        }

        this.hide();
    };

    private readonly onReturnToHomeMap = (): void => {
        if (document.querySelector("[data-smartguide-home]")) {
            void this.returnToHomeMap();
        }
    };

    private async returnToHomeMap(): Promise<void> {
        const homeElement = document.querySelector<HTMLElement>("[data-smartguide-home]");
        let state: HomeMapSearchState | undefined;
        try {
            state = JSON.parse(homeElement?.dataset.homeMapSearchState ?? "null") ?? undefined;
        } catch {
            state = undefined;
        }

        if (state?.context?.pageType !== "home") {
            await this.show();
            return;
        }

        this.cancelCurrentLocationFollow();
        this.clearSelectedMapFeature();
        this.currentContext = state.context;
        this.appliedContextKey = state.camera ? this.currentContextCameraKey : undefined;
        this.viewportReloadRequired = true;
        await this.show({ contextAlreadyRefreshed: true });
        if (!this.isVisible || !this.map) {
            return;
        }

        if (state.camera) {
            this.map.jumpTo?.(state.camera);
        }
        if (state.viewport) {
            this.element.dataset.smartguideMapCurrentViewport = state.viewport;
        }
    }

    private readonly onOpenContinentMap = (event: CustomEvent<HomeContinentMapEventDetail>): void => {
        const detail = event.detail ?? {};
        if (!hasUsableViewportBounds(detail.bounds)) {
            return;
        }

        this.currentContext = {
            pageType: "home",
            destinationId: null,
            language: detail.language?.trim() || "en",
            destinationName: detail.label?.trim() || "SmartGuide",
            itemsUrl: this.itemsUrl,
            nativeAppUrl: this.nativeAppUrl || null,
            isContentLoadedAutomatically: true,
            initialViewport: {
                kind: "continentBounds",
                bounds: detail.bounds,
                radiusMeters: 0
            }
        };
        this.appliedContextKey = undefined;
        this.updateChrome();
        void this.show({ contextAlreadyRefreshed: true });
    };

    private readonly onTourPoiSelectionChanged = (): void => {
        const scope = this.currentContext?.tourContextKey;
        if (scope && !getTourPoiSelectionState(document)?.getSnapshot(scope)) this.clearSelectedMapFeature();
        this.restoreTourPoiSelectionIfReady();
    };

    private restoreTourPoiSelectionIfReady(): void {
        const scope = this.currentContext?.tourContextKey;
        const snapshot = scope ? getTourPoiSelectionState(document)?.getSnapshot(scope) : null;
        if (snapshot) this.applyTourPoiSelection(snapshot);
    }

    private applyTourPoiSelection(snapshot: TourPoiSelectionSnapshot | null): void {
        const scope = this.currentContext?.tourContextKey;
        if (!scope || (snapshot && snapshot.tourContextKey !== scope)) return;
        if (!snapshot?.poiId) {
            this.clearSelectedMapFeature();
            this.suppressContextSelectionAfterClearingItem();
            this.elementState.appliedTourSelectionRevision = snapshot?.revision;
            if (this.isVisible && this.map && this.lastItems) this.renderItems(this.lastItems);
            return;
        }
        if (!this.isVisible || !this.map || (!this.styleLoaded && this.map.isStyleLoaded?.() === false)) return;
        const item = this.routePoiMapItems().find(candidate => candidate.id === snapshot.poiId);
        if (!item) return;
        if (this.elementState.appliedTourSelectionRevision === snapshot.revision && this.selectedItem?.id === item.id) return;
        const items = this.mergeWithTourRouteItems(this.lastItems ?? []);
        this.lastItems = items;
        this.selectedTourRoute = undefined;
        this.updateContextSelectionSuppressionForSelectedItem(item);
        this.selectedItem = item;
        this.renderMarkerCard(item);
        this.renderItems(items);
        this.fitSelectedTourPoiAboveOverlays(item);
        this.elementState.appliedTourSelectionRevision = snapshot.revision;
    }

    private readonly onDocumentPointerUp = (event: PointerEvent): void => {
        if (event.pointerType !== "touch" && event.pointerType !== "pen") {
            return;
        }

        const handledKey = this.handleBottomNavigationEvent(event);
        if (handledKey) {
            this.suppressedSyntheticClick = {
                key: handledKey,
                expiresAt: this.now() + 750
            };
        }
    };

    private handleBottomNavigationEvent(event: Event): string | undefined {
        const key = this.bottomNavigationKeyFromEvent(event);
        if (!key) {
            return undefined;
        }

        this.refreshContext();

        if (key === "map") {
            event.preventDefault();
            const openMode = this.mapOpenModeFromEvent(event);
            if (this.isVisible && !openMode) {
                this.setMapNavActive(true);
                return key;
            }

            const forceTourRouteCamera = openMode === "tour-route";
            const forceDestinationBoundsCamera = openMode === "destination-bounds";
            const forcePoiContextCamera = openMode === "poi-context";
            if (forceTourRouteCamera || forceDestinationBoundsCamera || forcePoiContextCamera) {
                this.cancelCurrentLocationFollow();
            }

            void this.show({
                forceTourRouteCamera,
                forceDestinationBoundsCamera,
                forcePoiContextCamera
            });
            return key;
        }

        if (key === "explore" && this.isVisible) {
            this.hide();
            if (/^\/profile(?:\/|$)/.test(window.location.pathname)) return undefined;
            event.preventDefault();
            event.stopImmediatePropagation();
            return key;
        }

        return undefined;
    }

    private bottomNavigationKeyFromEvent(event: Event): string | undefined {
        const target = event.target instanceof Element ? event.target : null;
        const navItem = target?.closest<HTMLElement>("[data-smartguide-bottom-navigation-key]");
        return navItem?.dataset.smartguideBottomNavigationKey;
    }

    private mapOpenModeFromEvent(event: Event): string | undefined {
        const target = event.target instanceof Element ? event.target : null;
        const navItem = target?.closest<HTMLElement>("[data-smartguide-map-open-mode]");
        return navItem?.dataset.smartguideMapOpenMode;
    }

    private shouldSuppressSyntheticClick(key: string): boolean {
        const suppressedClick = this.suppressedSyntheticClick;
        if (!suppressedClick || suppressedClick.key !== key) {
            return false;
        }

        if (this.now() > suppressedClick.expiresAt) {
            this.suppressedSyntheticClick = undefined;
            return false;
        }

        this.suppressedSyntheticClick = undefined;
        return true;
    }

    private now(): number {
        return window.performance?.now() ?? Date.now();
    }

    private readonly onTurboLoad = (): void => {
        const mapWasVisible = !this.element.hidden;
        const historyWantsMapVisible = this.historyStateMapVisible();
        const cachedPageWantsMapVisible = document.body.classList.contains("smartguide-map-visible");
        const keepCoveringSourcePage =
            this.element.dataset.smartGuidePermanentMapCoverSourcePageUrl === window.location.href;
        if (this.consumePreservedMapStateAfterNavigation()) {
            this.hide({ restoreScroll: false });
            return;
        }

        this.refreshContext();
        if (/^\/profile(?:\/|$)/.test(window.location.pathname)) {
            this.hide({ restoreScroll: false });
            return;
        }
        if (this.consumeExploreModeAfterMarkerDetailNavigation()) {
            if (keepCoveringSourcePage) {
                return;
            }

            this.hide({ restoreScroll: false });
            return;
        }

        if (mapWasVisible) {
            document.body.classList.add("smartguide-map-visible");
            this.setMapNavActive(true);
            this.writeHistoryStateMapVisible(true);
            this.applyContextCameraIfNeeded();
            this.requestCurrentLocation(false, { requestCompass: false });
            return;
        }

        if (historyWantsMapVisible === true || cachedPageWantsMapVisible) {
            void this.show({ restoreScrollFromHistory: true });
        }
    };

    private readonly onTurboRender = (): void => {
        this.refreshVisibleContext();
    };

    private readonly onBeforeRender = (): void => {
        if (!this.shouldPreserveStateDuringNavigation()) {
            return;
        }

        document.body.classList.remove("smartguide-map-visible");
        this.element.hidden = true;
        this.setMapNavActive(false);
    };

    private readonly onPageShow = (): void => {
        this.refreshVisibleContext();
    };

    private readonly onBeforeCache = (): void => {
        if (this.isVisible &&
            this.element.dataset.smartGuidePermanentMapCoverSourcePageUrl === window.location.href) {
            return;
        }

        document.body.classList.remove("smartguide-map-visible");
        this.markerCardElement.hidden = true;
    };

    private refreshVisibleContext(): void {
        if (this.shouldPreserveStateDuringNavigation()) {
            return;
        }

        this.refreshContext();
        if (this.element.dataset.smartGuidePermanentMapExploreAfterNavigation === "true") {
            return;
        }

        if (this.isVisible) {
            this.applyContextCameraIfNeeded();
        }
    }

    private shouldPreserveStateDuringNavigation(): boolean {
        return this.element.dataset.smartGuidePermanentMapPreserveStateAfterNavigation === "true";
    }

    private async show(options: {
        restoreScrollFromHistory?: boolean;
        contextAlreadyRefreshed?: boolean;
        forceTourRouteCamera?: boolean;
        forceDestinationBoundsCamera?: boolean;
        forcePoiContextCamera?: boolean;
    } = {}): Promise<void> {
        if (!options.contextAlreadyRefreshed) {
            this.refreshContext();
        }
        if (!this.isVisible) {
            const historyPageScroll = options.restoreScrollFromHistory
                ? this.historyStatePageScroll()
                : undefined;
            this.pageScrollBeforeMap = historyPageScroll ?? {
                left: window.scrollX,
                top: window.scrollY
            };
        }
        this.element.hidden = false;
        document.body.classList.add("smartguide-map-visible");
        this.setMapNavActive(true);
        this.writeHistoryStateMapVisible(true, this.pageScrollBeforeMap);
        this.updateChrome();

        if (!this.currentContext) {
            this.showUnavailable(webTranslations.mapUnavailable);
            return;
        }

        const mapWasInitialized = Boolean(this.map);
        const shouldCenterOnHomeLocation =
            !mapWasInitialized &&
            this.currentContext.pageType === "home" &&
            this.currentContext.initialViewport.kind === "currentLocation";
        this.requestCurrentLocation(shouldCenterOnHomeLocation, {
            centerZoom: shouldCenterOnHomeLocation ? homeDefaultZoom : undefined
        });

        await this.ensureMap();
        if (!this.isVisible || !this.map) {
            return;
        }

        this.renderUserLocationMarker();
        this.renderUserLocationAccuracy();
        this.centerOnUserLocationIfAvailable();
        this.renderCurrentLocationPointer();

        if (mapWasInitialized) {
            this.updateChrome();
            const restoredSelectedMapItemCamera = this.restoreSelectedMapItemCameraFromHistoryIfNeeded(
                options.restoreScrollFromHistory === true
            );
            if (options.forceTourRouteCamera) {
                this.applyTourRouteCamera();
            }
            if (!restoredSelectedMapItemCamera && this.shouldApplyTourRouteCameraOnInitializedOpen()) {
                this.applyTourRouteCamera();
            }
            if (options.forceDestinationBoundsCamera) {
                this.applyDestinationBoundsCamera();
            }
            if (options.forcePoiContextCamera) {
                this.applyPoiContextCamera();
            }
            if (!restoredSelectedMapItemCamera && options.contextAlreadyRefreshed) {
                this.applyContextCameraIfNeeded();
            }
            this.restoreSelectedMapFeature();
            this.renderRoute();
            if (this.viewportReloadRequired || !this.lastItems) {
                this.viewportReloadRequired = false;
                void this.loadViewportItems();
            }
            return;
        }

        this.applyContextCamera(this.currentContext.pageType === "home" ? 200 : 0);
        this.restoreTourPoiSelectionIfReady();
    }

    private shouldApplyTourRouteCameraOnInitializedOpen(): boolean {
        if (this.currentContext?.pageType !== "tour") {
            return false;
        }

        if (this.shouldPreserveSelectedTourPoiViewportOnTourOpen()) {
            return false;
        }

        return this.appliedContextKey !== this.currentContextCameraKey;
    }

    private shouldPreserveSelectedTourPoiViewportOnTourOpen(): boolean {
        const selectedItem = this.selectedItem;
        if (this.element.dataset.smartguideMapCurrentViewport !== "selectedTourPoi" ||
            !selectedItem ||
            !this.isTourPoiMapItem(selectedItem)) {
            return false;
        }

        const selectedItemId = selectedItem.id.trim().toLowerCase();
        return this.routePoiMapItems()
            .some(item => item.id.trim().toLowerCase() === selectedItemId);
    }

    private hide(options: { restoreScroll?: boolean } = {}): void {
        delete this.element.dataset.smartguideMapRenderedSelectedPoiId;
        const pageScroll = options.restoreScroll === false ? undefined : this.pageScrollBeforeMap;
        this.pageScrollBeforeMap = undefined;
        document.body.classList.remove("smartguide-map-visible");
        this.element.hidden = true;
        this.viewportReloadRequired ||= Boolean(this.viewportAbortController && !this.viewportAbortController.signal.aborted);
        this.destinationNavigationAbortController?.abort();
        this.destinationNavigationAbortController = undefined;
        this.viewportAbortController?.abort();
        this.viewportAbortController = undefined;
        this.viewportRequestKey = undefined;
        this.clearRenderedMarkerRefreshTimeout();
        this.clearRenderedMarkerIdleRefresh();
        this.stopGeolocationWatch();
        this.stopCompassHeadingUpdates();
        this.setMapNavActive(false);
        this.writeHistoryStateMapVisible(false);
        this.restorePageScrollIfNeeded(pageScroll);
    }

    private prepareExploreModeAfterMarkerDetailNavigation(event: MouseEvent, link: HTMLAnchorElement): void {
        if (!this.isPlainPrimaryLinkNavigation(event, link)) {
            return;
        }

        const href = link.getAttribute("href");
        if (!href || href === "#") {
            return;
        }

        this.prepareExploreModeAfterMarkerDetailNavigationState();
    }

    private prepareExploreModeAfterMarkerDetailNavigationState(): void {
        this.element.dataset.smartGuidePermanentMapExploreAfterNavigation = "true";
        if (this.selectedItem) {
            this.element.dataset.smartGuidePermanentMapPreserveSelectedMapItemAfterNavigation = "true";
            this.writeSelectedMapItemCameraToHistory();
        } else {
            delete this.element.dataset.smartGuidePermanentMapPreserveSelectedMapItemAfterNavigation;
        }
        this.writeHistoryStateMapVisible(true, this.pageScrollBeforeMap);
    }

    private homeDestinationNavigationUrl(event: MouseEvent, link: HTMLAnchorElement): string | undefined {
        if (!this.isPlainPrimaryLinkNavigation(event, link) ||
            this.currentContext?.pageType !== "home" ||
            !this.selectedItem ||
            !this.isDestinationMapItem(this.selectedItem)) {
            return undefined;
        }

        const href = link.getAttribute("href")?.trim();
        if (!href) {
            return undefined;
        }

        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin ||
            !/^\/destinations\/[^/]+\/?$/.test(url.pathname)) {
            return undefined;
        }

        return `${url.pathname}${url.search}`;
    }

    private isPlainPrimaryLinkNavigation(event: MouseEvent, link: HTMLAnchorElement): boolean {
        return !event.defaultPrevented &&
            event.button === 0 &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey &&
            !link.hasAttribute("download") &&
            (!link.target || link.target.toLowerCase() === "_self");
    }

    private async navigateToHomeDestination(link: HTMLAnchorElement, navigationUrl: string): Promise<void> {
        this.destinationNavigationAbortController?.abort();
        const abortController = new AbortController();
        this.destinationNavigationAbortController = abortController;
        link.setAttribute("aria-busy", "true");

        try {
            const response = await fetch(navigationUrl, {
                headers: { Accept: "application/json" },
                signal: abortController.signal
            });
            if (!response.ok) {
                throw new Error(`Destination navigation failed with status ${response.status}.`);
            }

            const payload = await response.json() as DestinationNavigationResponse;
            const canonicalUrl = payload.url?.trim();
            if (!canonicalUrl) {
                throw new Error("Destination navigation returned no canonical URL.");
            }

            if (this.destinationNavigationAbortController !== abortController) {
                return;
            }

            this.destinationNavigationAbortController = undefined;
            this.element.dataset.smartGuidePermanentMapCoverSourcePageUrl = window.location.href;
            this.prepareExploreModeAfterMarkerDetailNavigationState();
            this.visit(canonicalUrl);
        } catch (error) {
            if (!abortController.signal.aborted) {
                this.showUnavailable(webTranslations.mapItemsUnavailable);
            }
        } finally {
            link.removeAttribute("aria-busy");
            if (this.destinationNavigationAbortController === abortController) {
                this.destinationNavigationAbortController = undefined;
            }
        }
    }

    private historyStateMapVisible(): boolean | undefined {
        const state = window.history.state as Record<string, unknown> | null | undefined;
        const value = state?.[historyStateMapVisibleKey];
        if (typeof value === "boolean") {
            return value;
        }

        if (!this.isFullDocumentHistoryRestore()) {
            return undefined;
        }

        const storageKey = this.historyEntryMapVisibleStorageKey();
        if (!storageKey) {
            return undefined;
        }

        try {
            return window.sessionStorage.getItem(storageKey) === "true" ? true : undefined;
        } catch {
            return undefined;
        }
    }

    private historyStatePageScroll(): { left: number; top: number } | undefined {
        const state = window.history.state as Record<string, unknown> | null | undefined;
        const value = state?.[historyStatePageScrollKey] as Record<string, unknown> | undefined;
        const left = value?.left;
        const top = value?.top;
        if (typeof left !== "number" ||
            typeof top !== "number" ||
            !Number.isFinite(left) ||
            !Number.isFinite(top)) {
            return undefined;
        }

        return { left, top };
    }

    private writeHistoryStateMapVisible(isVisible: boolean, pageScroll?: { left: number; top: number }): void {
        const currentState = window.history.state;
        const nextState = currentState && typeof currentState === "object" && !Array.isArray(currentState)
            ? { ...currentState, [historyStateMapVisibleKey]: isVisible }
            : { [historyStateMapVisibleKey]: isVisible };
        if (isVisible &&
            pageScroll &&
            Number.isFinite(pageScroll.left) &&
            Number.isFinite(pageScroll.top)) {
            nextState[historyStatePageScrollKey] = {
                left: pageScroll.left,
                top: pageScroll.top
            };
        } else {
            delete nextState[historyStatePageScrollKey];
        }
        window.history.replaceState(nextState, "", window.location.href);
        this.persistHistoryEntryMapVisibility(isVisible);
    }

    private persistHistoryEntryMapVisibility(isVisible: boolean): void {
        const storageKey = this.historyEntryMapVisibleStorageKey();
        if (!storageKey) {
            return;
        }

        try {
            if (isVisible) {
                window.sessionStorage.setItem(storageKey, "true");
            } else {
                window.sessionStorage.removeItem(storageKey);
            }
        } catch {
            // History restoration remains best-effort when browser storage is unavailable.
        }
    }

    private historyEntryMapVisibleStorageKey(): string | undefined {
        const state = window.history.state as Record<string, unknown> | null | undefined;
        const turbo = state?.turbo as Record<string, unknown> | undefined;
        const restorationIndex = turbo?.restorationIndex;
        if (typeof restorationIndex !== "number" || !Number.isInteger(restorationIndex)) {
            return undefined;
        }

        return `${historyEntryMapVisibleStoragePrefix}${restorationIndex}:${window.location.pathname}${window.location.search}`;
    }

    private isFullDocumentHistoryRestore(): boolean {
        const navigation = window.performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
        return navigation?.type === "back_forward";
    }

    private writeSelectedMapItemCameraToHistory(): void {
        const camera = this.readCurrentSelectedMapItemCamera();
        if (!camera) {
            return;
        }

        const currentState = window.history.state;
        const nextState = currentState && typeof currentState === "object" && !Array.isArray(currentState)
            ? { ...currentState, [historyStateSelectedMapItemCameraKey]: camera }
            : { [historyStateSelectedMapItemCameraKey]: camera };
        window.history.replaceState(nextState, "", window.location.href);
    }

    private readCurrentSelectedMapItemCamera(): SelectedMapItemCamera | undefined {
        const camera = this.readCurrentMapCamera();
        if (!camera || !this.selectedItem) {
            return undefined;
        }

        return {
            ...camera,
            mapPath: window.location.pathname,
            selectedItem: { ...this.selectedItem },
            viewport: this.element.dataset.smartguideMapCurrentViewport
        };
    }

    private readCurrentMapCamera(): MapCamera | undefined {
        if (!this.map) {
            return undefined;
        }

        const center = this.map.getCenter?.();
        const zoom = Number(this.map.getZoom?.());
        const bearing = Number(this.map.getBearing?.() ?? 0);
        const pitch = Number(this.map.getPitch?.() ?? 0);
        const padding = this.map.getPadding?.() ?? {};
        const longitude = Number(center?.lng);
        const latitude = Number(center?.lat);
        const top = Number(padding.top ?? 0);
        const right = Number(padding.right ?? 0);
        const bottom = Number(padding.bottom ?? 0);
        const left = Number(padding.left ?? 0);
        if (![longitude, latitude, zoom, bearing, pitch, top, right, bottom, left].every(Number.isFinite)) {
            return undefined;
        }

        return {
            center: [longitude, latitude],
            zoom,
            bearing,
            pitch,
            padding: { top, right, bottom, left }
        };
    }

    private restoreSelectedMapItemCameraFromHistoryIfNeeded(historyRestore = false): boolean {
        if (!historyRestore && !this.restoreSelectedTourPoiCameraOnNextTourShow) {
            return false;
        }

        this.restoreSelectedTourPoiCameraOnNextTourShow = false;
        const state = window.history.state as Record<string, unknown> | null | undefined;
        const camera = state?.[historyStateSelectedMapItemCameraKey] as Partial<SelectedMapItemCamera> | undefined;
        if (!this.map ||
            this.currentContext?.pageType === "home" ||
            camera?.mapPath !== window.location.pathname ||
            !Array.isArray(camera.center) ||
            camera.center.length !== 2) {
            return false;
        }

        const savedItem = camera.selectedItem as SmartGuideMapItem | undefined;
        const selectedItemId = savedItem?.id?.trim().toLowerCase();
        if (!savedItem ||
            !selectedItemId ||
            !Number.isFinite(Number(savedItem?.latitude)) ||
            !Number.isFinite(Number(savedItem?.longitude))) {
            return false;
        }

        const availableItems = this.mergeWithTourRouteItems(this.lastItems ?? []);
        const selectedItem = availableItems.find(item => item.id.trim().toLowerCase() === selectedItemId) ?? savedItem;
        if (!availableItems.some(item => item.id.trim().toLowerCase() === selectedItemId)) {
            this.lastItems = [selectedItem, ...availableItems];
        }

        const longitude = Number(camera.center[0]);
        const latitude = Number(camera.center[1]);
        const zoom = Number(camera.zoom);
        const bearing = Number(camera.bearing);
        const pitch = Number(camera.pitch);
        const top = Number(camera.padding?.top);
        const right = Number(camera.padding?.right);
        const bottom = Number(camera.padding?.bottom);
        const left = Number(camera.padding?.left);
        if (![longitude, latitude, zoom, bearing, pitch, top, right, bottom, left].every(Number.isFinite)) {
            return false;
        }

        this.map.jumpTo?.({
            center: [longitude, latitude],
            zoom,
            bearing,
            pitch,
            padding: { top, right, bottom, left }
        });
        this.selectedItem = selectedItem;
        this.element.dataset.smartguideMapCurrentViewport = camera.viewport?.trim() ||
            (this.isTourPoiMapItem(selectedItem) ? "selectedTourPoi" : "selectedMapItem");
        this.appliedContextKey = this.currentContextCameraKey;
        return true;
    }

    private markerDetailLinkCanNavigate(link: HTMLAnchorElement): boolean {
        const href = link.getAttribute("href")?.trim();
        return Boolean(href) && href !== "#";
    }

    private markerDetailLinkTargetsCurrentPoi(): boolean {
        if (this.currentContext?.pageType !== "poi" && this.currentContext?.pageType !== "tourPoi") {
            return false;
        }

        const currentPoiId = this.currentContext.selectedPoiId?.trim().toLowerCase();
        const selectedMapItemId = this.selectedItem?.id.trim().toLowerCase();
        return Boolean(currentPoiId) && currentPoiId === selectedMapItemId;
    }

    private openSearchFromMap(): void {
        if (this.currentContext?.pageType === "home") {
            const homeElement = document.querySelector<HTMLElement>("[data-smartguide-home]");
            if (homeElement) {
                const state: HomeMapSearchState = {
                    context: this.currentContext,
                    camera: this.readCurrentMapCamera(),
                    viewport: this.element.dataset.smartguideMapCurrentViewport
                };
                homeElement.dataset.homeMapSearchState = JSON.stringify(state);
            }
            this.hide({ restoreScroll: false });
            document.dispatchEvent(new Event(openHomeSearchEventName));
            return;
        }

        const url = this.destinationSightsUrlFromCurrentPath();
        if (!url) {
            return;
        }

        this.element.dataset.smartGuidePermanentMapExploreAfterNavigation = "true";
        this.element.dataset.smartGuidePermanentMapPreserveStateAfterNavigation = "true";
        this.writeSelectedMapItemCameraToHistory();
        this.writeHistoryStateMapVisible(true, this.pageScrollBeforeMap);
        this.visit(url);
    }

    private destinationSightsUrlFromCurrentPath(): string | undefined {
        if (!this.currentContext || this.currentContext.pageType === "home") {
            return undefined;
        }

        const compactBase = smartGuideFrontendSettings.integrationCompactBase?.trim();
        if (compactBase !== undefined && (window.location.pathname === compactBase ||
            window.location.pathname.startsWith(`${compactBase}/`))) {
            const relativePath = window.location.pathname.slice(compactBase.length);
            const compactMatch = /^\/([^/?#]+)/.exec(relativePath);
            if (!compactMatch) {
                return undefined;
            }

            const url = new URL(`${compactBase}/${compactMatch[1]}/sights`, window.location.origin);
            url.searchParams.set("noDefaultFilter", "true");
            return `${url.pathname}${url.search}`;
        }

        const match = /^\/([^/]+)\/destinations\/([^/]+)\/([^/?#]+)/.exec(window.location.pathname);
        if (!match) {
            return undefined;
        }

        const [, language, destinationUniqueId, destinationSlug] = match;
        const url = new URL(`/${language}/destinations/${destinationUniqueId}/${destinationSlug}/sights`, window.location.origin);
        url.searchParams.set("noDefaultFilter", "true");
        return `${url.pathname}${url.search}`;
    }

    private consumePreservedMapStateAfterNavigation(): boolean {
        if (this.element.dataset.smartGuidePermanentMapExploreAfterNavigation !== "true" ||
            this.element.dataset.smartGuidePermanentMapPreserveStateAfterNavigation !== "true") {
            return false;
        }

        delete this.element.dataset.smartGuidePermanentMapExploreAfterNavigation;
        delete this.element.dataset.smartGuidePermanentMapPreserveStateAfterNavigation;
        return true;
    }

    private consumeExploreModeAfterMarkerDetailNavigation(): boolean {
        if (this.element.dataset.smartGuidePermanentMapExploreAfterNavigation !== "true") {
            return false;
        }

        const preserveSelectedMapItem = this.element.dataset.smartGuidePermanentMapPreserveSelectedMapItemAfterNavigation === "true";
        delete this.element.dataset.smartGuidePermanentMapExploreAfterNavigation;
        delete this.element.dataset.smartGuidePermanentMapPreserveSelectedMapItemAfterNavigation;
        delete this.element.dataset.smartGuidePermanentMapCoverSourcePageUrl;
        if (!preserveSelectedMapItem) {
            this.appliedContextKey = undefined;
        }
        return true;
    }

    private restorePageScrollIfNeeded(pageScroll: { left: number; top: number } | undefined): void {
        if (!pageScroll || !Number.isFinite(pageScroll.left) || !Number.isFinite(pageScroll.top)) {
            return;
        }

        if (Math.round(window.scrollX) === Math.round(pageScroll.left) &&
            Math.round(window.scrollY) === Math.round(pageScroll.top)) {
            return;
        }

        window.scrollTo({ left: pageScroll.left, top: pageScroll.top, behavior: "auto" });
    }

    private async ensureMap(): Promise<void> {
        if (this.map) {
            this.bindMapHandlers();
            this.map.resize?.();
            this.renderMapCompass();
            this.renderUserLocationMarker();
            this.renderUserLocationAccuracy();
            this.renderCurrentLocationPointer();
            return;
        }

        if (!this.webGlIsAvailable()) {
            this.showUnavailable(webTranslations.mapRenderingUnavailable);
            return;
        }

        this.mapModule ??= await import("maplibre-gl");
        this.map = new this.mapModule.Map({
            container: this.canvasElement,
            style: this.styleUrl,
            center: this.initialMapCenter(),
            zoom: 12,
            attributionControl: false,
            logoPosition: "bottom-left"
        });
        this.bindMapHandlers();
        this.renderMapCompass();
        if (this.map.isStyleLoaded?.() !== false) {
            this.styleLoaded = true;
        } else {
            this.map.on("load", () => {
                this.styleLoaded = true;
                this.renderRoute();
                if (this.lastItems) {
                    this.renderItems(this.lastItems);
                }
                this.restoreTourPoiSelectionIfReady();
                this.renderUserLocationMarker();
                this.renderUserLocationAccuracy();
                this.renderCurrentLocationPointer();
            });
        }
    }

    private initialMapCenter(): [number, number] {
        const viewport = this.currentContext?.initialViewport;
        if (hasUsableCoordinates(viewport?.center)) {
            return [viewport.center.longitude, viewport.center.latitude];
        }
        if (hasUsableBoundingBox(viewport?.boundingBox)) {
            const { northEast, southWest } = viewport.boundingBox;
            return [
                (northEast.longitude + southWest.longitude) / 2,
                (northEast.latitude + southWest.latitude) / 2
            ];
        }
        if (hasUsableViewportBounds(viewport?.bounds)) {
            const { west, south, east, north } = viewport.bounds;
            return [(west + east) / 2, (south + north) / 2];
        }
        return this.userLocationLngLat ?? [14.421, 50.087];
    }

    private bindMapHandlers(): void {
        if (!this.map || this.mapHandlersBound) {
            return;
        }

        this.map.on("moveend", this.onMapMoveEnd);
        this.map.on("movestart", this.onMapMoveStart);
        this.map.on("click", markerLayerId, this.onMapFeatureClick);
        this.map.on("click", practicalMarkerLayerId, this.onMapFeatureClick);
        this.map.on("click", selectedMarkerLayerId, this.onMapFeatureClick);
        this.map.on("click", routeHitLayerId, this.onTourRouteClick);
        this.map.on("click", this.onMapClick);
        this.map.on("rotate", this.onMapRotate);
        this.mapHandlersBound = true;
    }

    private unbindMapHandlers(): void {
        if (!this.map || !this.mapHandlersBound) {
            return;
        }

        if (typeof this.map.off === "function") {
            this.map.off("moveend", this.onMapMoveEnd);
            this.map.off("movestart", this.onMapMoveStart);
            this.map.off("click", markerLayerId, this.onMapFeatureClick);
            this.map.off("click", practicalMarkerLayerId, this.onMapFeatureClick);
            this.map.off("click", selectedMarkerLayerId, this.onMapFeatureClick);
            this.map.off("click", routeHitLayerId, this.onTourRouteClick);
            this.map.off("click", this.onMapClick);
            this.map.off("rotate", this.onMapRotate);
        }

        this.mapHandlersBound = false;
    }

    private readonly onMapMoveEnd = (): void => {
        this.publishRenderedCamera();
        if (!this.isVisible) {
            return;
        }

        this.queueRenderedMarkerRefresh();
        void this.loadViewportItems();
        this.renderCurrentLocationPointer();
    };

    private readonly onMapMoveStart = (event?: { originalEvent?: Event }): void => {
        this.publishRenderedCamera();
        if (!event?.originalEvent) {
            return;
        }

        this.followUserLocation = false;
    };

    private readonly onMapRotate = (): void => {
        this.renderMapCompass();
        this.updateUserLocationBearing();
        this.renderCurrentLocationPointer();
    };

    private renderMapCompass(): void {
        const button = this.element.querySelector<HTMLButtonElement>(
            "[data-smart-guide-permanent-map-compass]"
        );
        if (!button) {
            return;
        }

        const bearing = Number(this.map?.getBearing?.() ?? 0);
        const roundedBearing = Number.isFinite(bearing) ? Math.round(bearing) : 0;
        button.hidden = roundedBearing === 0;
        const icon = button.querySelector<HTMLElement>("[data-smart-guide-permanent-map-compass-icon]");
        if (icon) {
            icon.style.transform = `rotate(${-roundedBearing}deg)`;
        }
    }

    private resetMapBearing(): void {
        if (!this.map) {
            return;
        }

        this.map.easeTo?.({
            bearing: 0,
            duration: 200
        });
        const button = this.element.querySelector<HTMLButtonElement>(
            "[data-smart-guide-permanent-map-compass]"
        );
        if (button) {
            button.hidden = true;
        }
    }

    private readonly onMapFeatureClick = (event: any): void => {
        const feature = event?.features?.[0];
        const itemId = this.renderedMarkerId(feature);
        const item = itemId
            ? this.lastItems?.find(candidate => candidate.id === itemId)
            : undefined;
        if (!item) {
            return;
        }

        this.selectedTourRoute = undefined;
        this.updateContextSelectionSuppressionForSelectedItem(item);
        this.selectedItem = item;
        this.renderMarkerCard(item);
        this.renderItems([...(this.lastItems ?? [])]);
        this.notifyTourTraySelection(item);
    };

    private readonly onTourRouteClick = (event: any): void => {
        if (this.mapClickHitMarkerFeature(event)) {
            return;
        }

        const routeId = this.renderedMarkerId(event?.features?.[0]);
        const route = routeId
            ? this.destinationTourRoutes().find(candidate => candidate.id === routeId)
            : undefined;
        if (!route) {
            return;
        }

        this.selectedItem = undefined;
        this.selectedTourRoute = route;
        this.renderTourRouteCard(route);
        this.renderRoute();
        this.fitTourRouteBounds(route);
    };

    private readonly onMapClick = (event: any): void => {
        if (this.mapClickHitInteractiveFeature(event)) {
            return;
        }

        if (this.selectedTourRoute) {
            this.selectedTourRoute = undefined;
            if (this.markerCardElement.dataset.smartguidePermanentMapMarkerType === "tour") {
                this.markerCardElement.hidden = true;
            }
            this.renderRoute();
            return;
        }

        if (this.selectedItem) {
            const clearedItem = this.selectedItem;
            const wasTourPoi = this.isTourPoiMapItem(clearedItem);
            this.selectedItem = undefined;
            this.suppressContextSelectionAfterClearingItem();
            if (this.markerCardElement.dataset.smartguidePermanentMapMarkerType !== "tour") {
                this.markerCardElement.hidden = true;
            }
            this.renderItems([...(this.lastItems ?? [])]);
            if (wasTourPoi) {
                this.notifyTourTraySelectionCleared();
            }
        }
    };

    private mapClickHitInteractiveFeature(event: any): boolean {
        const layers = [markerLayerId, practicalMarkerLayerId, selectedMarkerLayerId];
        if (this.map?.getLayer?.(routeLayerId)) {
            layers.unshift(routeLayerId);
        }
        if (this.map?.getLayer?.(routeHitLayerId)) {
            layers.unshift(routeHitLayerId);
        }

        const features = this.map?.queryRenderedFeatures?.(event?.point, {
            layers
        }) ?? event?.features ?? [];
        return features.some((feature: any) => Boolean(this.renderedMarkerId(feature)));
    }

    private mapClickHitMarkerFeature(event: any): boolean {
        if (!event?.point) {
            return false;
        }

        const features = this.map?.queryRenderedFeatures?.(event.point, {
            layers: [markerLayerId, practicalMarkerLayerId, selectedMarkerLayerId]
        }) ?? [];
        return features.some((feature: any) => Boolean(this.renderedMarkerId(feature)));
    }

    private notifyTourTraySelection(item: SmartGuideMapItem): void {
        if (!this.isTourPoiMapItem(item)) {
            return;
        }

        const tourContextKey = this.currentContext?.tourContextKey;
        if (tourContextKey) getTourPoiSelectionState(document)?.select({ tourContextKey, poiId: item.id, origin: "map" });
    }

    private updateContextSelectionSuppressionForSelectedItem(item: SmartGuideMapItem): void {
        const selectedPoiId = this.currentContext?.selectedPoiId?.trim();
        if (!selectedPoiId) {
            return;
        }

        if (selectedPoiId.toLowerCase() === item.id.toLowerCase()) {
            this.suppressedContextSelectedPoiId = undefined;
            return;
        }

        this.suppressedContextSelectedPoiId = selectedPoiId;
    }

    private suppressContextSelectionAfterClearingItem(): void {
        const selectedPoiId = this.currentContext?.selectedPoiId?.trim();
        if (!selectedPoiId) {
            return;
        }

        this.suppressedContextSelectedPoiId = selectedPoiId;
    }

    private notifyTourTraySelectionCleared(): void {
        const tourContextKey = this.currentContext?.tourContextKey;
        if (tourContextKey) getTourPoiSelectionState(document)?.select({ tourContextKey, poiId: null, origin: "map" });
    }

    private clearSelectedMapFeature(): void {
        delete this.element.dataset.smartguideMapRenderedSelectedPoiId;
        this.selectedItem = undefined;
        this.selectedTourRoute = undefined;
        this.markerCardElement.hidden = true;
    }

    private restoreSelectedMapFeature(): void {
        this.restoreTourPoiSelectionIfReady();
        if (!this.selectedItem) {
            return;
        }

        this.renderMarkerCard(this.selectedItem);
        if (this.lastItems) {
            this.renderItems([...this.lastItems]);
        }
    }

    private applyContextCameraIfNeeded(): void {
        const contextKey = this.currentContextCameraKey;
        if (contextKey && this.appliedContextKey === contextKey) {
            this.updateChrome();
            this.restoreSelectedMapFeature();
            return;
        }

        this.applyContextCamera();
    }

    private applyContextCamera(duration = 200): void {
        if (!this.map || !this.currentContext) {
            return;
        }

        const viewport = this.currentContext.initialViewport;
        if (this.applySelectedTourPoiCamera(duration)) {
            // Selected tour POI pages need their first camera fit to account for the permanent tray and preview card.
        } else if (this.applyTourRouteCameraForInitialContext(duration)) {
            // Tour pages should fit the route into the map area above the permanent tour tray.
        } else if (viewport.kind === "currentLocation") {
            if (this.userLocationLngLat) {
                this.map.easeTo?.({
                    center: this.userLocationLngLat,
                    zoom: homeDefaultZoom,
                    duration
                });
            } else {
                this.map.fitBounds([[homeDefaultBounds.west, homeDefaultBounds.south], [homeDefaultBounds.east, homeDefaultBounds.north]], {
                    padding: { top: 80, right: 24, bottom: 120, left: 24 },
                    duration
                });
            }
        } else if (viewport.kind === "continentBounds" && hasUsableViewportBounds(viewport.bounds)) {
            this.map.fitBounds([[viewport.bounds.west, viewport.bounds.south], [viewport.bounds.east, viewport.bounds.north]], {
                padding: { top: 120, right: 40, bottom: 160, left: 40 },
                duration
            });
        } else if (viewport.kind === "poiContext" && hasUsableCoordinates(viewport.center)) {
            this.map.fitBounds(boundsAround(viewport.center, viewport.radiusMeters || 100), {
                padding: { top: 120, right: 60, bottom: 180, left: 60 },
                duration
            });
        } else if (hasUsableBoundingBox(viewport.boundingBox)) {
            this.map.fitBounds(destinationBoundingBoxToLngLatBounds(viewport.boundingBox), {
                padding: { top: 140, right: 40, bottom: 180, left: 40 },
                duration
            });
        } else if (hasUsableCoordinates(viewport.center)) {
            this.map.fitBounds(boundsAround(viewport.center, viewport.radiusMeters || 100), {
                padding: { top: 140, right: 40, bottom: 180, left: 40 },
                duration
            });
        } else {
            this.showUnavailable(webTranslations.mapCameraUnavailable);
            return;
        }

        if (this.element.dataset.smartguideMapCurrentViewport !== "selectedTourPoi" &&
            this.element.dataset.smartguideMapCurrentViewport !== "tourRoute") {
            this.element.dataset.smartguideMapCurrentViewport = viewport.kind;
        }
        this.appliedContextKey = this.currentContextCameraKey;
        this.updateChrome();
        this.renderRoute();
        void this.loadViewportItems();
    }

    private applyTourRouteCamera(): void {
        if (!this.map || !this.currentContext) {
            return;
        }

        if (this.fitCurrentTourRouteCamera()) {
            this.renderRoute();
            void this.loadViewportItems();
            return;
        }

        this.applyContextCamera();
    }

    private applyTourRouteCameraForInitialContext(duration = 200): boolean {
        if (this.currentContext?.pageType !== "tour") {
            return false;
        }

        return this.fitCurrentTourRouteCamera(duration);
    }

    private fitCurrentTourRouteCamera(duration = 200): boolean {
        if (!this.map || !this.currentContext) {
            return false;
        }

        const routeBoundingBox = this.routeCoordinateBoundingBox();
        if (!hasUsableBoundingBox(routeBoundingBox)) {
            return false;
        }

        this.map.fitBounds(destinationBoundingBoxToLngLatBounds(routeBoundingBox), {
            padding: this.tourCameraPadding(),
            duration
        });
        this.element.dataset.smartguideMapCurrentViewport = "tourRoute";
        this.appliedContextKey = this.currentContextCameraKey;
        return true;
    }

    private applyDestinationBoundsCamera(): void {
        if (!this.map || !this.currentContext) {
            return;
        }

        const viewport = this.currentContext.initialViewport;
        if (!hasUsableBoundingBox(viewport.boundingBox)) {
            this.applyContextCamera();
            return;
        }

        this.map.fitBounds(destinationBoundingBoxToLngLatBounds(viewport.boundingBox), {
            padding: { top: 140, right: 40, bottom: 180, left: 40 },
            duration: 200
        });
        this.element.dataset.smartguideMapCurrentViewport = "destinationBounds";
        this.appliedContextKey = this.currentContextCameraKey;
        this.updateChrome();
        this.renderRoute();
        void this.loadViewportItems();
    }

    private applyPoiContextCamera(): void {
        if (!this.map || !this.currentContext) {
            return;
        }

        const viewport = this.currentContext.initialViewport;
        if (this.currentContext.pageType !== "poi" &&
            this.currentContext.pageType !== "tourPoi") {
            this.applyContextCamera();
            return;
        }

        if (viewport.kind !== "poiContext" || !hasUsableCoordinates(viewport.center)) {
            this.applyContextCamera();
            return;
        }

        this.map.fitBounds(boundsAround(viewport.center, viewport.radiusMeters || 100), {
            padding: { top: 120, right: 60, bottom: 180, left: 60 },
            duration: 200
        });
        this.element.dataset.smartguideMapCurrentViewport = "poiContext";
        this.appliedContextKey = this.currentContextCameraKey;
        this.updateChrome();
        this.renderRoute();
        void this.loadViewportItems();
    }

    private routeCoordinateBoundingBox(): DestinationMapBoundingBox | undefined {
        const coordinates = (this.currentContext?.routeCoordinates ?? [])
            .filter(hasUsableCoordinates);
        if (coordinates.length < 2) {
            return undefined;
        }

        const latitudes = coordinates.map(coordinate => coordinate.latitude);
        const longitudes = coordinates.map(coordinate => coordinate.longitude);
        return {
            northEast: {
                latitude: Math.max(...latitudes),
                longitude: Math.max(...longitudes)
            },
            southWest: {
                latitude: Math.min(...latitudes),
                longitude: Math.min(...longitudes)
            }
        };
    }

    private fitTourRouteBounds(route: DestinationTourRoute): void {
        if (!this.map) {
            return;
        }

        const boundingBox = this.tourRouteBoundingBox(route);
        if (!hasUsableBoundingBox(boundingBox)) {
            return;
        }

        this.map.fitBounds(destinationBoundingBoxToLngLatBounds(boundingBox), {
            padding: { top: 120, right: 40, bottom: 150, left: 40 },
            duration: 200
        });
        this.element.dataset.smartguideMapCurrentViewport = "tourRoute";
    }

    private applySelectedTourPoiCamera(duration = 200): boolean {
        const item = this.selectedTourPoiMapItem();
        if (!item) {
            return false;
        }

        this.selectedItem = item;
        this.renderMarkerCard(item);
        this.fitSelectedTourPoiAboveOverlays(item, duration);
        return true;
    }

    private selectedTourPoiMapItem(): SmartGuideMapItem | undefined {
        const scope = this.currentContext?.tourContextKey;
        const snapshot = scope ? getTourPoiSelectionState(document)?.getSnapshot(scope) : null;
        const selectedPoiId = snapshot ? snapshot.poiId : this.currentContext?.selectedPoiId?.trim();
        if (!selectedPoiId || (this.currentContext?.pageType !== "tourPoi" && !snapshot)) {
            return undefined;
        }

        return this.routePoiMapItems()
            .find(item => item.id.toLowerCase() === selectedPoiId.toLowerCase());
    }

    private tourRouteBoundingBox(route: DestinationTourRoute): DestinationMapBoundingBox | undefined {
        if (hasUsableBoundingBox(route.boundingBox)) {
            return route.boundingBox;
        }

        const coordinates = (route.routeCoordinates ?? []).filter(hasUsableCoordinates);
        if (coordinates.length < 2) {
            return undefined;
        }

        const latitudes = coordinates.map(coordinate => coordinate.latitude);
        const longitudes = coordinates.map(coordinate => coordinate.longitude);
        return {
            northEast: {
                latitude: Math.max(...latitudes),
                longitude: Math.max(...longitudes)
            },
            southWest: {
                latitude: Math.min(...latitudes),
                longitude: Math.min(...longitudes)
            }
        };
    }

    private fitSelectedTourPoiAboveOverlays(item: SmartGuideMapItem, duration = tourPoiCameraDurationMs): void {
        if (!this.map ||
            !this.isTourPoiMapItem(item) ||
            !Number.isFinite(item.latitude) ||
            !Number.isFinite(item.longitude)) {
            return;
        }

        const bounds = getTourPoiCameraBounds(this.currentContext?.routePoiMarkers ?? [], item.id);
        if (!bounds) return;
        this.cancelCurrentLocationFollow();
        const layout = this.measureTourPoiCameraLayout();
        this.elementState.tourPoiCameraLayout = layout;
        this.map.fitBounds(bounds, {
            padding: getTourPoiCameraPadding(layout),
            duration,
            maxZoom: tourPoiCameraMaxZoom
        });
        this.element.dataset.smartguideMapCurrentViewport = "selectedTourPoi";
        this.observeCameraLayout();
        this.publishRenderedCamera();
    }

    private cameraOverlays(): { top: HTMLElement[]; bottom: HTMLElement[] } {
        return {
            top: Array.from(this.element.querySelectorAll<HTMLElement>(".smartguide-permanent-map__top, [data-smart-guide-permanent-map-search]")),
            bottom: [this.markerCardElement, ...Array.from(document.querySelectorAll<HTMLElement>(
                "#smartguide-tour-tray, .smartguide-tour-tray, [data-persistent-audio-player], [data-smartguide-bottom-navigation]"))]
        };
    }

    private measureTourPoiCameraLayout(): TourPoiCameraLayout {
        const canvas = this.canvasElement.getBoundingClientRect();
        const overlays = this.cameraOverlays();
        const occupied = (nodes: HTMLElement[], top: boolean): number => Math.max(0, ...nodes.map(node => {
            if (node.closest("[hidden]")) return 0;
            const rect = node.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0 || rect.right <= canvas.left || rect.left >= canvas.right ||
                rect.bottom <= canvas.top || rect.top >= canvas.bottom) return 0;
            return top ? Math.min(canvas.bottom, rect.bottom) - canvas.top : canvas.bottom - Math.max(canvas.top, rect.top);
        }));
        return { width: canvas.width, height: canvas.height, topOccupied: occupied(overlays.top, true), bottomOccupied: occupied(overlays.bottom, false) };
    }

    private observeCameraLayout(): void {
        if (typeof ResizeObserver === "undefined") return;
        this.cameraLayoutObserver ??= new ResizeObserver(this.queueCameraLayoutFit);
        const overlays = this.cameraOverlays();
        const elements = new Set([this.canvasElement, ...overlays.top, ...overlays.bottom]);
        for (const element of this.cameraLayoutElements) {
            if (!elements.has(element)) this.cameraLayoutObserver.unobserve(element);
        }
        for (const element of elements) {
            if (!this.cameraLayoutElements.has(element)) this.cameraLayoutObserver.observe(element);
        }
        this.cameraLayoutElements = elements;
    }

    private readonly queueCameraLayoutFit = (): void => {
        if (this.cameraLayoutFrame !== undefined) return;
        this.cameraLayoutFrame = window.requestAnimationFrame(() => {
            this.cameraLayoutFrame = undefined;
            if (!this.isVisible || !this.map || !this.selectedItem || this.selectedTourPoiMapItem()?.id !== this.selectedItem.id ||
                (!this.styleLoaded && this.map.isStyleLoaded?.() === false)) return;
            const layout = this.measureTourPoiCameraLayout();
            const previous = this.elementState.tourPoiCameraLayout;
            if (JSON.stringify(layout) === JSON.stringify(previous)) return;
            if (layout.width !== previous?.width || layout.height !== previous?.height) this.map.resize?.();
            this.fitSelectedTourPoiAboveOverlays(this.selectedItem);
        });
    };

    private publishRenderedCamera(): void {
        const bounds = this.readViewportBounds();
        const zoom = Number(this.map?.getZoom?.());
        if (!bounds || !Number.isFinite(zoom)) {
            delete this.element.dataset.smartguideMapRenderedCamera;
            delete this.element.dataset.smartguideMapCameraMoving;
            return;
        }
        this.element.dataset.smartguideMapCameraMoving = String(this.map?.isMoving?.() === true);
        const item = this.selectedItem;
        const point = item && hasUsableCoordinates(item) ? this.map?.project?.([item.longitude, item.latitude]) : undefined;
        this.element.dataset.smartguideMapRenderedCamera = JSON.stringify({ zoom, bounds,
            selectedPoiScreenPosition: point && Number.isFinite(point.x) && Number.isFinite(point.y) ? { x: point.x, y: point.y } : null });
    }

    private elementHeight(element: HTMLElement | null | undefined): number {
        const rectHeight = Number(element?.getBoundingClientRect?.().height ?? 0);
        if (Number.isFinite(rectHeight) && rectHeight > 0) {
            return rectHeight;
        }

        const offsetHeight = Number(element?.offsetHeight ?? 0);
        return Number.isFinite(offsetHeight) && offsetHeight > 0 ? offsetHeight : 0;
    }

    private tourCameraPadding(): { top: number; right: number; bottom: number; left: number } {
        const tray = document.querySelector<HTMLElement>(".smartguide-tour-tray:not([hidden])");
        const trayHeight = this.elementHeight(tray);
        return {
            top: 90,
            right: 50,
            bottom: Math.max(220, trayHeight + 70),
            left: 50
        };
    }

    private async loadViewportItems(): Promise<void> {
        if (!this.isVisible || !this.map || !this.currentContext) {
            return;
        }

        const bounds = this.readViewportBounds();
        const zoom = Number(this.map.getZoom?.() ?? 12);
        if (!bounds || !Number.isFinite(zoom)) {
            return;
        }

        const localDestinationItemsCacheKey = this.localDestinationItemsCacheKey;
        const cachedLocalDestinationItems = localDestinationItemsCacheKey
            ? this.localDestinationItemCaches.get(localDestinationItemsCacheKey)
            : undefined;
        const includeActiveDestinationItems = !localDestinationItemsCacheKey || !cachedLocalDestinationItems;

        const url = new URL(this.currentContext.itemsUrl || this.itemsUrl, window.location.origin);
        if (this.currentContext.destinationId) {
            url.searchParams.set("destinationId", this.currentContext.destinationId);
        }
        if (this.currentContext.pageType === "home") {
            url.searchParams.set("global", "true");
        }
        url.searchParams.set("language", this.currentContext.language);
        url.searchParams.set("limit", "150");
        if (!includeActiveDestinationItems) {
            url.searchParams.set("includeActiveDestinationItems", "false");
        }
        for (const label of this.activeLabels) {
            url.searchParams.append("labels", label);
        }
        for (const category of this.activeCategories) {
            url.searchParams.append("categories", category);
        }

        const requestUrls = viewportBoundsToBboxes(bounds, zoom).map(bbox => {
            const requestUrl = new URL(url);
            requestUrl.searchParams.set("bbox", bbox);
            return requestUrl.toString();
        });
        const requestKey = JSON.stringify([requestUrls, document.body.dataset.preferredLanguages ?? "[]"]);
        if (this.viewportRequestKey === requestKey && !this.viewportAbortController?.signal.aborted) {
            return;
        }

        this.viewportAbortController?.abort();
        const abortController = new AbortController();
        this.viewportAbortController = abortController;
        this.viewportRequestKey = requestKey;

        try {
            const responses = await Promise.all(requestUrls.map(async requestUrl => {
                const response = await fetch(requestUrl, {
                    headers: { accept: "application/json" },
                    signal: abortController.signal
                });
                if (!response.ok) {
                    throw new Error(`Map items request failed with ${response.status}`);
                }

                return await response.json() as SmartGuideMapItemsResponse;
            }));
            if (abortController.signal.aborted) {
                return;
            }

            const uniqueItems = new Map<string, SmartGuideMapItem>();
            for (const response of responses) {
                for (const item of response.items ?? []) {
                    uniqueItems.set(item.id.toLowerCase(), item);
                }
            }
            const viewportItems = this.mergeWithCachedLocalDestinationItems(
                Array.from(uniqueItems.values()),
                localDestinationItemsCacheKey,
                includeActiveDestinationItems
            );
            this.lastItems = this.mergeWithTourRouteItems(viewportItems);
            this.selectContextPoiIfAvailable(this.lastItems);
            this.renderItems(this.lastItems);
            this.restoreTourPoiSelectionIfReady();
            this.clearMapUnavailable();
        } catch (error) {
            if (abortController.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) {
                return;
            }

            this.showUnavailable(webTranslations.mapItemsUnavailable);
        } finally {
            if (this.viewportAbortController === abortController) {
                this.viewportAbortController = undefined;
                this.viewportRequestKey = undefined;
            }
        }
    }

    private mergeWithCachedLocalDestinationItems(
        responseItems: SmartGuideMapItem[],
        localDestinationItemsCacheKey: string | undefined,
        includeActiveDestinationItems: boolean
    ): SmartGuideMapItem[] {
        if (!localDestinationItemsCacheKey) {
            return responseItems;
        }

        const destinationItems = responseItems.filter(item => this.isDestinationMapItem(item));
        if (includeActiveDestinationItems) {
            const localDestinationItems = responseItems.filter(item => !this.isDestinationMapItem(item));
            this.localDestinationItemCaches.set(localDestinationItemsCacheKey, localDestinationItems);
            return [...localDestinationItems, ...destinationItems];
        }

        return [
            ...(this.localDestinationItemCaches.get(localDestinationItemsCacheKey) ?? []),
            ...destinationItems
        ];
    }

    private mergeWithTourRouteItems(items: SmartGuideMapItem[]): SmartGuideMapItem[] {
        const routeItems = this.routePoiMapItems();
        if (routeItems.length === 0) {
            return items;
        }

        const routeItemIds = new Set(routeItems.map(item => item.id.toLowerCase()));
        return [
            ...routeItems,
            ...items.filter(item => !routeItemIds.has(item.id.toLowerCase()))
        ];
    }

    private routePoiMapItems(): SmartGuideMapItem[] {
        return (this.currentContext?.routePoiMarkers ?? [])
            .map(marker => this.routePoiMapItem(marker))
            .filter((item): item is SmartGuideMapItem => item !== undefined);
    }

    private routePoiMapItem(marker: DestinationRoutePoiMarker): SmartGuideMapItem | undefined {
        const id = marker.id?.trim();
        const latitude = Number(marker.latitude);
        const longitude = Number(marker.longitude);
        const order = Number(marker.order);
        if (!id || !Number.isFinite(latitude) || !Number.isFinite(longitude) || !Number.isFinite(order) || order <= 0) {
            return undefined;
        }

        const title = marker.name?.trim() || `Place ${order}`;
        const audio = marker.audio;
        const audioUrl = audio?.url?.trim();
        return {
            id,
            destinationId: this.currentContext?.destinationId ?? "",
            title,
            latitude,
            longitude,
            category: "SeeAndDo",
            subcategory: null,
            labels: [],
            priority: 10_000_000 - order,
            layer: 3,
            imageUrl: marker.imageUrl?.trim() || null,
            hasAudio: Boolean(audioUrl),
            detailUrl: marker.canonicalUrl?.trim() || null,
            disableDetail: !marker.canonicalUrl?.trim(),
            mapIconId: null,
            mapIconUrl: null,
            contentSource: "tour",
            tourOrder: order,
            tourColor: tourMarkerBlue,
            audioId: audio?.id?.trim() || null,
            audioUrl: audioUrl || null,
            audioTitle: audioUrl ? audio?.title?.trim() || title : null,
            audioArtist: audioUrl ? audio?.artist?.trim() || "SmartGuide" : null,
            audioArtworkUrl: audioUrl ? audio?.artworkUrl?.trim() || marker.imageUrl?.trim() || null : null,
            audioDetailUrl: audioUrl ? audio?.detailUrl?.trim() || marker.canonicalUrl?.trim() || null : null,
            isSubscriberOnly: marker.isSubscriberOnly === true
        };
    }

    private isDestinationMapItem(item: SmartGuideMapItem): boolean {
        return item.contentSource?.toLowerCase() === "destination";
    }

    private isTourPoiMapItem(item: SmartGuideMapItem): boolean {
        return Number.isFinite(Number(item.tourOrder));
    }

    private isLockedTourPoiMapItem(item: SmartGuideMapItem): boolean {
        return this.isTourPoiMapItem(item) && item.isSubscriberOnly === true;
    }

    private selectContextPoiIfAvailable(items: SmartGuideMapItem[]): void {
        const scope = this.currentContext?.tourContextKey;
        if (scope && getTourPoiSelectionState(document)?.getSnapshot(scope)) return;
        const selectedPoiId = this.currentContext?.selectedPoiId?.trim();
        if (!selectedPoiId) {
            return;
        }

        if (this.selectedItem || this.selectedTourRoute) {
            return;
        }

        if (this.suppressedContextSelectedPoiId?.toLowerCase() === selectedPoiId.toLowerCase()) {
            return;
        }

        const selectedItem = items.find(item => item.id.toLowerCase() === selectedPoiId.toLowerCase());
        if (!selectedItem) {
            return;
        }

        this.selectedItem = selectedItem;
        this.renderMarkerCard(selectedItem);
    }

    private renderItems(items: SmartGuideMapItem[]): void {
        if (!this.map) {
            return;
        }
        if (!this.styleLoaded && this.map.isStyleLoaded?.() === false) {
            this.lastItems = items;
            return;
        }

        const markerItems = items
            .filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
            .sort((first, second) => {
                const priorityDifference = (second.priority ?? 0) - (first.priority ?? 0);
                return priorityDifference !== 0
                    ? priorityDifference
                    : first.title.localeCompare(second.title);
            });

        const featureCollection = {
            type: "FeatureCollection",
            features: markerItems
                .map((item, index) => {
                    const markerIcon = this.resolveMarkerSymbolIcon(item);
                    this.ensureMarkerImage(markerIcon);
                    const isTourPoi = this.isTourPoiMapItem(item);
                    const title = this.isPracticalMapItem(item)
                        ? ""
                        : isTourPoi ? this.tourPoiMarkerTitle(item) : item.title;

                    return {
                        type: "Feature",
                        id: item.id,
                        geometry: {
                            type: "Point",
                            coordinates: [item.longitude, item.latitude]
                        },
                        properties: {
                            id: item.id,
                            title,
                            category: item.category,
                            priority: item.priority,
                            nativePriority: 8_000_000 + index,
                            type: this.isDestinationMapItem(item) ? "destination" : "sight",
                            layer: item.layer,
                            large: markerIcon.id,
                            iconAnchor: title ? "bottom" : "center",
                            textColor: isTourPoi ? (item.tourColor || tourMarkerBlue) : "#303030",
                            textHaloColor: "#ffffff",
                            tourOrder: item.tourOrder ?? null,
                            selected: this.selectedItem?.id === item.id
                        }
                    };
                })
        };

        const existingSource = this.map.getSource?.(sourceId);
        if (existingSource?.setData) {
            existingSource.setData(featureCollection);
        } else {
            this.map.addSource(sourceId, {
                type: "geojson",
                data: featureCollection
            });
        }

        this.ensureMarkerLayers();
        this.renderPhotoDomMarkers(markerItems);
        this.queueRenderedMarkerRefresh();
        this.element.dataset.smartguideMapItemCount = items.length.toString();
        this.observeCameraLayout();
        this.publishRenderedCamera();
        this.repaintMap();
    }

    private tourPoiMarkerTitle(item: SmartGuideMapItem): string {
        const order = Number(item.tourOrder);
        const title = Number.isFinite(order) && order > 0
            ? `${order}.${item.title}`
            : item.title;

        return title.length > 28 ? `${title.slice(0, 28)}...` : title;
    }

    private renderRoute(): void {
        if (!this.map) {
            return;
        }

        if (!this.styleLoaded && this.map.isStyleLoaded?.() === false) {
            return;
        }

        const existingSource = this.map.getSource?.(routeSourceId);
        const routeFeatures = this.destinationTourRouteFeatures();
        const featureCollection = routeFeatures.length > 0
            ? {
                type: "FeatureCollection",
                features: routeFeatures
            }
            : this.activeTourRouteFeatureCollection();

        if (featureCollection.features.length === 0) {
            existingSource?.setData?.(this.emptyFeatureCollection());
            return;
        }

        if (existingSource?.setData) {
            existingSource.setData(featureCollection);
        } else {
            this.map.addSource(routeSourceId, {
                type: "geojson",
                data: featureCollection
            });
        }

        this.ensureRouteLayers();
    }

    private destinationTourRouteFeatures(): any[] {
        const selectedId = this.selectedTourRoute?.id;
        return this.destinationTourRoutes()
            .slice()
            .reverse()
            .map(route => ({
                type: "Feature",
                id: route.id,
                geometry: {
                    type: "LineString",
                    coordinates: route.routeCoordinates
                        .filter(hasUsableCoordinates)
                        .map(coordinate => [coordinate.longitude, coordinate.latitude])
                },
                properties: {
                    id: route.id,
                    color: this.tourRouteLineColor(route, selectedId),
                    label: route.name,
                    lineScale: selectedId ? (route.id === selectedId ? 2 : 0.5) : 1,
                    offset: 0,
                    opacity: 1
                }
            }));
    }

    private destinationTourRoutes(): DestinationTourRoute[] {
        if (this.currentContext?.pageType !== "destination") {
            return [];
        }

        return (this.currentContext.tourRoutes ?? [])
            .filter(route => route.id?.trim())
            .filter(route => (route.routeCoordinates ?? []).filter(hasUsableCoordinates).length >= 2);
    }

    private activeTourRouteFeatureCollection(): any {
        const routeCoordinates = (this.currentContext?.routeCoordinates ?? [])
            .filter(hasUsableCoordinates);
        if (routeCoordinates.length < 2) {
            return this.emptyFeatureCollection();
        }

        return {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: routeCoordinates.map(coordinate => [coordinate.longitude, coordinate.latitude])
                    },
                    properties: {
                        color: tourMarkerBlue,
                        lineScale: 1,
                        offset: 0,
                        opacity: 1
                    }
                }
            ]
        };
    }

    private tourRouteLineColor(route: DestinationTourRoute, selectedId: string | undefined): string {
        const color = route.color?.trim() || tourMarkerBlue;
        if (!selectedId || route.id === selectedId) {
            return color;
        }

        return this.colorWithAlpha(color, 0.5);
    }

    private colorWithAlpha(color: string, alpha: number): string {
        const rgb = this.rgbComponents(color);
        return rgb
            ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
            : color;
    }

    private rgbComponents(color: string): [number, number, number] | undefined {
        const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hex) {
            const value = hex[1].length === 3
                ? hex[1].split("").map(part => `${part}${part}`).join("")
                : hex[1];
            return [
                parseInt(value.slice(0, 2), 16),
                parseInt(value.slice(2, 4), 16),
                parseInt(value.slice(4, 6), 16)
            ];
        }

        const rgb = color.trim().match(/^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*[0-9.]+)?\s*\)$/i);
        if (!rgb) {
            return undefined;
        }

        return [
            Math.round(Number(rgb[1])),
            Math.round(Number(rgb[2])),
            Math.round(Number(rgb[3]))
        ];
    }

    private emptyFeatureCollection(): any {
        return {
            type: "FeatureCollection",
            features: []
        };
    }

    private ensureRouteLayers(): void {
        if (!this.map) {
            return;
        }

        const beforeMarkerLayer = this.map.getLayer?.(markerLayerId) ? markerLayerId : undefined;
        if (!this.map.getLayer?.(routeLayerId)) {
            this.map.addLayer({
                id: routeLayerId,
                type: "line",
                source: routeSourceId,
                layout: {
                    "line-cap": "round",
                    "line-join": "miter"
                },
                paint: {
                    "line-color": ["get", "color"],
                    "line-width": ["*", routeLineWidth, ["coalesce", ["get", "lineScale"], 1]],
                    "line-offset": ["*", ["coalesce", ["get", "offset"], 0], routeLineWidth],
                    "line-opacity": ["coalesce", ["get", "opacity"], 1]
                }
            }, beforeMarkerLayer);
        }

        if (!this.map.getLayer?.(routeHitLayerId)) {
            this.map.addLayer({
                id: routeHitLayerId,
                type: "line",
                source: routeSourceId,
                layout: {
                    "line-cap": "round",
                    "line-join": "miter"
                },
                paint: {
                    "line-color": "#000000",
                    "line-width": routeHitLineWidth,
                    "line-offset": ["*", ["coalesce", ["get", "offset"], 0], routeLineWidth],
                    "line-opacity": 0
                }
            }, beforeMarkerLayer);
        }
    }

    private ensureMarkerLayers(): void {
        if (!this.map) {
            return;
        }

        const markerLayerLayout = {
            "icon-image": ["get", "large"],
            "icon-size": nativeMarkerIconSize,
            "icon-allow-overlap": false,
            "text-allow-overlap": false,
            "icon-optional": false,
            "icon-anchor": ["get", "iconAnchor"],
            "text-field": ["get", "title"],
            "text-font": ["Noto Sans Regular"],
            "text-size": 14,
            "text-anchor": "top",
            "text-max-width": 10,
            "text-offset": [0, 0.1],
            "symbol-sort-key": ["get", "nativePriority"]
        };
        const markerLayerPaint = {
            "text-color": ["get", "textColor"],
            "text-halo-color": ["get", "textHaloColor"],
            "text-halo-width": 2,
            "text-halo-blur": 0
        };

        if (!this.map.getLayer?.(markerLayerId)) {
            this.map.addLayer({
                id: markerLayerId,
                type: "symbol",
                source: sourceId,
                filter: ["!=", ["get", "layer"], 1],
                layout: markerLayerLayout,
                paint: markerLayerPaint
            });
        }

        if (!this.map.getLayer?.(practicalMarkerLayerId)) {
            this.map.addLayer({
                id: practicalMarkerLayerId,
                type: "symbol",
                source: sourceId,
                minzoom: 14,
                filter: ["==", ["get", "layer"], 1],
                layout: markerLayerLayout,
                paint: markerLayerPaint
            });
        }

        if (!this.map.getLayer?.(selectedMarkerLayerId)) {
            this.map.addLayer({
                id: selectedMarkerLayerId,
                type: "symbol",
                source: sourceId,
                filter: ["==", ["get", "selected"], true],
                layout: {
                    ...markerLayerLayout,
                    "icon-allow-overlap": true,
                    "text-allow-overlap": true
                },
                paint: markerLayerPaint
            });
        }
    }

    private resolvePhotoMarkerIcon(item: SmartGuideMapItem): NativeMarkerIcon | undefined {
        if (this.isPracticalMapItem(item) || (this.selectedItem?.id === item.id && !this.isDestinationMapItem(item))) {
            return undefined;
        }

        const mapIconUrl = item.mapIconUrl?.trim();
        const directMapIconUrl = mapIconUrl
            ? this.directMarkerIconUrl(mapIconUrl)
            : undefined;
        if (directMapIconUrl) {
            return {
                id: item.mapIconId?.trim() || this.markerIconId(item),
                url: directMapIconUrl
            };
        }

        const generatedIconUrl = this.generatedMapIconUrl(item);
        if (generatedIconUrl) {
            return {
                id: item.mapIconId?.trim() || this.markerIconId(item),
                url: generatedIconUrl
            };
        }

        return undefined;
    }

    private isPracticalMapItem(item: SmartGuideMapItem): boolean {
        return ["other", "practical"].includes((item.category || "").toLowerCase());
    }

    private resolveMarkerSymbolIcon(item: SmartGuideMapItem): NativeMarkerIcon {
        if (this.isPracticalMapItem(item)) {
            const subcategory = (item.subcategory || "")
                .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
                .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
                .replace(/[^a-z0-9]+/gi, "_")
                .replace(/^_+|_+$/g, "")
                .toLowerCase();
            const id = this.selectedItem?.id === item.id
                ? "selected_poi_other"
                : practicalMarkerSubcategories.has(subcategory) ? `other_${subcategory}` : "other";
            const pathBase = smartGuideFrontendSettings.integrationDeliveryKind === "FullWeb"
                ? smartGuideFrontendSettings.integrationPublicPathBase || ""
                : "";
            return { id, url: `${pathBase}/assets/map-markers/${id}.png` };
        }

        const id = this.placeholderMarkerIconId(item);
        const cachedIcon = this.placeholderMarkerIcons.get(id);
        if (cachedIcon) {
            return cachedIcon;
        }

        const image = this.placeholderMarkerImageData(item);
        const icon = {
            id,
            image,
            url: image ? undefined : this.placeholderMarkerIconDataUrl(item)
        };
        this.placeholderMarkerIcons.set(id, icon);

        return icon;
    }

    private markerIconId(item: SmartGuideMapItem): string {
        return `smartguide-map-icon-${item.id}`;
    }

    private placeholderMarkerIconId(item: SmartGuideMapItem): string {
        if (this.selectedItem?.id === item.id && !this.isDestinationMapItem(item)) {
            return "smartguide-map-placeholder-selected-poi";
        }

        if (this.isTourPoiMapItem(item)) {
            return "smartguide-map-placeholder-tour-poi";
        }

        const kind = this.isDestinationMapItem(item)
            ? "destination"
            : (item.category || "poi").toLowerCase();

        return `smartguide-map-placeholder-${kind}`;
    }

    private generatedMapIconUrl(item: SmartGuideMapItem): string | undefined {
        const imageUrl = item.imageUrl?.trim();
        if (!imageUrl?.startsWith(originalSmartGuideImagePrefix)) {
            return undefined;
        }

        const storagePath = imageUrl.slice(originalSmartGuideImagePrefix.length);
        const iconType = this.isDestinationMapItem(item) ? "Destination" : "Poi";
        const outlineColor = this.isTourPoiMapItem(item) ? "Tour" : "Default";

        return `${generatedMapIconBaseUrl}?imageStoragePath=${storagePath}&iconType=${iconType}&outlineColor=${outlineColor}&environment=production`;
    }

    private directMarkerIconUrl(iconUrl: string): string | undefined {
        try {
            const url = new URL(iconUrl);
            if (url.protocol !== "https:" ||
                url.host.toLowerCase() !== "du8wuy497pbt7.cloudfront.net" ||
                !url.searchParams.get("imageStoragePath")) {
                return undefined;
            }

            return iconUrl;
        } catch {
            return undefined;
        }
    }

    private placeholderMarkerIconDataUrl(item: SmartGuideMapItem): string {
        if (this.selectedItem?.id === item.id && !this.isDestinationMapItem(item)) {
            return this.selectedPoiMarkerIconDataUrl();
        }

        const isDestination = this.isDestinationMapItem(item);
        const width = isDestination ? 144 : 112;
        const height = isDestination ? 144 : 124;
        const centerX = width / 2;
        const centerY = isDestination ? height / 2 : 56;
        const radius = isDestination ? 62 : 48;
        const fillColor = this.placeholderMarkerColor(item);
        const pointer = isDestination
            ? ""
            : `<path d="M${centerX - 14} 96 L${centerX} 118 L${centerX + 14} 96 Z" fill="#ffffff" stroke="#d6d6d6" stroke-width="3"/>`;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
<filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
<feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="0.22"/>
</filter>
<g filter="url(#shadow)">
${pointer}
<circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="#ffffff" stroke="#d6d6d6" stroke-width="3"/>
<circle cx="${centerX}" cy="${centerY}" r="${radius - 12}" fill="${fillColor}"/>
</g>
</svg>`;

        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    private selectedPoiMarkerIconDataUrl(): string {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="112" height="124" viewBox="0 0 112 124">
<filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
<feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="0.22"/>
</filter>
<g filter="url(#shadow)">
<path d="M42 96 L56 118 L70 96 Z" fill="${selectedPoiRed}" stroke="${selectedPoiRed}" stroke-width="3"/>
<circle cx="56" cy="56" r="43" fill="#ffffff" stroke="${selectedPoiRed}" stroke-width="10"/>
<circle cx="56" cy="56" r="16" fill="${selectedPoiRed}"/>
</g>
</svg>`;

        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    private placeholderMarkerImageData(item: SmartGuideMapItem): ImageData | undefined {
        if (this.selectedItem?.id === item.id && !this.isDestinationMapItem(item)) {
            return this.selectedPoiMarkerImageData();
        }

        const isDestination = this.isDestinationMapItem(item);
        const width = isDestination ? 144 : 112;
        const height = isDestination ? 144 : 124;
        const centerX = width / 2;
        const centerY = isDestination ? height / 2 : 56;
        const radius = isDestination ? 62 : 48;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) {
            return undefined;
        }

        context.save();
        context.fillStyle = "#ffffff";
        context.strokeStyle = "#d6d6d6";
        context.lineWidth = 3;

        if (!isDestination) {
            context.beginPath();
            context.moveTo(centerX - 14, 96);
            context.lineTo(centerX, 118);
            context.lineTo(centerX + 14, 96);
            context.closePath();
            context.fill();
            context.stroke();
        }

        context.beginPath();
        context.arc(centerX, centerY, radius, 0, Math.PI * 2);
        context.fill();
        context.stroke();

        context.fillStyle = this.placeholderMarkerColor(item);
        context.beginPath();
        context.arc(centerX, centerY, radius - 12, 0, Math.PI * 2);
        context.fill();

        const imageData = context.getImageData(0, 0, width, height);
        context.restore();

        return imageData;
    }

    private selectedPoiMarkerImageData(): ImageData | undefined {
        const width = 112;
        const height = 124;
        const centerX = width / 2;
        const centerY = 56;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) {
            return undefined;
        }

        context.save();
        context.fillStyle = selectedPoiRed;
        context.strokeStyle = selectedPoiRed;
        context.lineWidth = 3;
        context.beginPath();
        context.moveTo(centerX - 14, 96);
        context.lineTo(centerX, 118);
        context.lineTo(centerX + 14, 96);
        context.closePath();
        context.fill();
        context.stroke();

        context.fillStyle = "#ffffff";
        context.strokeStyle = selectedPoiRed;
        context.lineWidth = 10;
        context.beginPath();
        context.arc(centerX, centerY, 43, 0, Math.PI * 2);
        context.fill();
        context.stroke();

        context.fillStyle = selectedPoiRed;
        context.beginPath();
        context.arc(centerX, centerY, 16, 0, Math.PI * 2);
        context.fill();

        const imageData = context.getImageData(0, 0, width, height);
        context.restore();

        return imageData;
    }

    private placeholderMarkerColor(item: SmartGuideMapItem): string {
        if (this.isDestinationMapItem(item)) {
            return "#2b2b2b";
        }

        if (this.isTourPoiMapItem(item)) {
            return tourMarkerBlue;
        }

        switch ((item.category || "").toLowerCase()) {
            case "eat":
                return "#f09b23";
            case "shop":
                return "#7b61ff";
            case "sleep":
                return "#3483fa";
            case "party":
                return "#d22f90";
            default:
                return "#2b9f04";
        }
    }

    private ensureMarkerImage(markerIcon: NativeMarkerIcon): void {
        if (!this.map || !markerIcon.id) {
            return;
        }

        if (this.loadedMarkerImageIds.has(markerIcon.id)) {
            return;
        }

        if (this.map.hasImage?.(markerIcon.id)) {
            this.loadedMarkerImageIds.add(markerIcon.id);
            return;
        }

        if (!markerIcon.image) {
            if (markerIcon.url && !this.loadingMarkerImageIds.has(markerIcon.id)) {
                const map = this.map;
                this.loadingMarkerImageIds.add(markerIcon.id);
                void map.loadImage(markerIcon.url)
                    .then((result: { data: ImageData | HTMLImageElement | ImageBitmap }) => {
                        if (this.map === map) {
                            this.ensureMarkerImage({ id: markerIcon.id, image: result.data });
                            this.queueRenderedMarkerRefresh();
                        }
                    })
                    .catch(() => { /* A missing marker asset must not break map interaction. */ })
                    .finally(() => this.loadingMarkerImageIds.delete(markerIcon.id));
            }
            return;
        }

        try {
            this.map.addImage?.(markerIcon.id, markerIcon.image);
            this.loadedMarkerImageIds.add(markerIcon.id);
            this.repaintMap();
        } catch {
            // Missing a placeholder should not break map interaction.
        }
    }

    private repaintMap(): void {
        try {
            this.map?.triggerRepaint?.();
        } catch {
            // Repaint is a rendering hint; source updates and navigation should keep working if it fails.
        }
    }

    private queueRenderedMarkerRefresh(): void {
        if (!this.renderedMarkerRefreshQueued) {
            this.renderedMarkerRefreshQueued = true;
            void Promise.resolve().then(() => {
                this.renderedMarkerRefreshQueued = false;
                this.refreshRenderedMarkerIds();
            });
        }

        if (!this.renderedMarkerIdleRefreshQueued && typeof this.map?.once === "function") {
            this.renderedMarkerIdleRefreshQueued = true;
            this.map.once("idle", this.onRenderedMarkerIdle);
        }

        if (this.renderedMarkerRefreshTimeoutId !== undefined) {
            return;
        }

        this.renderedMarkerRefreshTimeoutId = window.setTimeout(() => {
            this.renderedMarkerRefreshTimeoutId = undefined;
            this.refreshRenderedMarkerIds();
        }, 500);
    }

    private readonly onRenderedMarkerIdle = (): void => {
        this.renderedMarkerIdleRefreshQueued = false;
        this.refreshRenderedMarkerIds();
    };

    private refreshRenderedMarkerIds(): void {
        this.updateRenderedTourPoiSelection();
        if (!this.map || !this.lastItems || typeof this.map.queryRenderedFeatures !== "function") {
            return;
        }

        const renderedFeatures = this.map.queryRenderedFeatures(undefined, {
            layers: [markerLayerId, practicalMarkerLayerId, selectedMarkerLayerId]
        }) ?? [];
        const renderedIds = renderedFeatures
            .map((feature: any) => this.renderedMarkerId(feature))
            .filter((id: string | undefined): id is string => Boolean(id));
        if (renderedIds.length === 0 && this.renderedMarkerIds.size === 0) {
            this.renderPhotoDomMarkers(this.lastItems);
            return;
        }

        const retainedIds = new Set<string>(renderedIds);
        if (this.sameSet(this.renderedMarkerIds, retainedIds)) {
            this.renderPhotoDomMarkers(this.lastItems);
            return;
        }

        this.renderedMarkerIds = retainedIds;
        this.renderPhotoDomMarkers(this.lastItems);
    }

    private renderedMarkerId(feature: any): string | undefined {
        const featureId = feature?.properties?.id ?? feature?.id;
        return featureId == null ? undefined : String(featureId);
    }

    private updateRenderedTourPoiSelection(): void {
        delete this.element.dataset.smartguideMapRenderedSelectedPoiId;
        const item = this.selectedItem;
        if (!this.isVisible || !item || !this.isTourPoiMapItem(item) || !this.map?.getLayer?.(selectedMarkerLayerId)) return;
        const rendered = this.map.queryRenderedFeatures?.(undefined, { layers: [selectedMarkerLayerId] }) ?? [];
        if (rendered.some((feature: any) => this.renderedMarkerId(feature) === item.id)) {
            this.element.dataset.smartguideMapRenderedSelectedPoiId = item.id;
        }
    }

    private renderPhotoDomMarkers(markerItems: SmartGuideMapItem[]): void {
        if (!this.map || !this.mapModule?.Marker) {
            return;
        }

        const bounds = this.readViewportBounds();
        const retainedMarkerIds = new Set<string>();
        for (const item of markerItems) {
            const photoIcon = this.resolvePhotoMarkerIcon(item);
            if (!photoIcon?.url || !this.renderedMarkerIds.has(item.id)) {
                continue;
            }

            const iconUrl = photoIcon.url;
            const firstWorld = bounds ? Math.ceil((bounds.west - item.longitude) / 360) : 0;
            const lastWorld = bounds ? Math.floor((bounds.east - item.longitude) / 360) : 0;
            const nearestWorld = bounds
                ? Math.round(((bounds.west + bounds.east) / 2 - item.longitude) / 360)
                : 0;
            const startWorld = firstWorld <= lastWorld ? firstWorld : nearestWorld;
            const endWorld = firstWorld <= lastWorld ? lastWorld : nearestWorld;
            for (let world = startWorld; world <= endWorld; world++) {

                const markerId = `${item.id}:${world}`;
                const coordinates: [number, number] = [item.longitude + world * 360, item.latitude];
                retainedMarkerIds.add(markerId);
                const existingMarker = this.photoMarkerDomMarkers.get(markerId);
                if (existingMarker) {
                    const currentLongitude = Number(existingMarker.marker.getLngLat?.().lng);
                    const wrappedIntoAnotherWorld = Number.isFinite(currentLongitude)
                        && Math.abs(currentLongitude - coordinates[0]) > 180;
                    if (!wrappedIntoAnotherWorld) {
                        if (existingMarker.iconUrl !== iconUrl) {
                            this.updatePhotoMarkerElement(existingMarker.marker.getElement?.(), iconUrl);
                            existingMarker.iconUrl = iconUrl;
                        }
                        continue;
                    }

                    existingMarker.marker.remove?.();
                }

                const marker = new this.mapModule.Marker({
                    element: this.createPhotoMarkerElement(iconUrl, this.isDestinationMapItem(item)),
                    anchor: "bottom"
                })
                    .setLngLat(coordinates)
                    .addTo(this.map);
                this.photoMarkerDomMarkers.set(markerId, {
                    marker,
                    iconUrl
                });
            }
        }

        for (const [markerId, markerState] of Array.from(this.photoMarkerDomMarkers.entries())) {
            if (retainedMarkerIds.has(markerId)) {
                continue;
            }

            markerState.marker.remove?.();
            this.photoMarkerDomMarkers.delete(markerId);
        }
    }

    private createPhotoMarkerElement(iconUrl: string, isDestination: boolean): HTMLElement {
        const element = document.createElement("div");
        element.className = "smartguide-permanent-map__photo-marker";
        element.style.width = isDestination ? "72px" : "56px";
        element.style.height = isDestination ? "72px" : "62px";
        const image = document.createElement("img");
        image.alt = "";
        image.draggable = false;
        element.append(image);
        this.updatePhotoMarkerElement(element, iconUrl);
        return element;
    }

    private updatePhotoMarkerElement(element: Element | undefined, iconUrl: string): void {
        const image = element?.querySelector<HTMLImageElement>("img");
        if (image) {
            image.src = iconUrl;
        }
    }

    private sameSet(left: Set<string>, right: Set<string>): boolean {
        return left.size === right.size && Array.from(left).every(value => right.has(value));
    }

    private clearRenderedMarkerRefreshTimeout(): void {
        if (this.renderedMarkerRefreshTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.renderedMarkerRefreshTimeoutId);
        this.renderedMarkerRefreshTimeoutId = undefined;
    }

    private clearRenderedMarkerIdleRefresh(): void {
        if (!this.renderedMarkerIdleRefreshQueued) {
            return;
        }

        this.map?.off?.("idle", this.onRenderedMarkerIdle);
        this.renderedMarkerIdleRefreshQueued = false;
    }

    private renderMarkerCard(item: SmartGuideMapItem): void {
        const card = this.markerCardElement;
        const isDestination = this.isDestinationMapItem(item);
        const canOpenDetail = Boolean(item.detailUrl) && !item.disableDetail;
        card.dataset.smartguidePermanentMapMarkerType = isDestination ? "destination" : "poi";
        card.dataset.smartguidePermanentMapSelectedId = item.id;
        this.markerTitleElement.textContent = item.title;

        const image = card.querySelector<HTMLImageElement>("[data-smart-guide-permanent-map-target='markerImage']");
        if (image) {
            image.src = item.imageUrl || "";
            image.hidden = !item.imageUrl;
        }

        const link = card.querySelector<HTMLAnchorElement>("[data-smart-guide-permanent-map-target='markerLink']");
        if (link) {
            if (canOpenDetail) {
                link.href = item.detailUrl!;
                link.removeAttribute("aria-disabled");
            } else {
                link.removeAttribute("href");
                link.setAttribute("aria-disabled", "true");
            }
            link.hidden = false;
            link.setAttribute("aria-label", canOpenDetail ? formatTranslation(webTranslations.openItemFormat, item.title) : item.title);
        }

        const distance = card.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerDistance']");
        const distanceText = card.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerDistanceText']");
        const formattedDistance = this.formattedDistanceFromUser(item);
        if (distance && distanceText) {
            distance.hidden = !formattedDistance;
            distanceText.textContent = formattedDistance ?? "";
        }
        this.hideTourMeta();

        const chevron = card.querySelector<HTMLAnchorElement>("[data-smart-guide-permanent-map-target='markerChevron']");
        if (chevron) {
            chevron.hidden = !isDestination || !canOpenDetail;
            if (canOpenDetail) {
                chevron.href = item.detailUrl!;
                chevron.removeAttribute("aria-disabled");
            } else {
                chevron.removeAttribute("href");
                chevron.setAttribute("aria-disabled", "true");
            }
            chevron.setAttribute("aria-label", canOpenDetail ? formatTranslation(webTranslations.openItemFormat, item.title) : item.title);
        }

        const audioButton = card.querySelector<HTMLButtonElement>("[data-smart-guide-permanent-map-target='markerAudioButton']");
        if (audioButton) {
            const media = this.markerAudioMedia(item);
            const isLockedTourPoi = this.isLockedTourPoiMapItem(item);
            audioButton.hidden = isDestination || !media;
            audioButton.disabled = !media && !isLockedTourPoi;
            if (isLockedTourPoi) {
                audioButton.setAttribute("aria-disabled", "true");
                audioButton.setAttribute("aria-label", formatTranslation(webTranslations.openItemFormat, item.title));
                audioButton.dataset.smartGuidePermanentMapMarkerAudioLocked = "true";
            } else {
                audioButton.removeAttribute("aria-disabled");
                delete audioButton.dataset.smartGuidePermanentMapMarkerAudioLocked;
                audioButton.setAttribute("aria-label", formatTranslation(webTranslations.playItemAudioFormat, item.title));
            }
        }

        const tourButton = card.querySelector<HTMLButtonElement>("[data-smart-guide-permanent-map-target='markerTourButton']");
        if (tourButton) {
            tourButton.hidden = isDestination || this.isTourPoiMapItem(item) || this.currentContext?.hidePlanTour === true;
            tourButton.setAttribute("aria-label", formatTranslation(webTranslations.planTourWithItemFormat, item.title));
        }

        card.hidden = this.isCurrentTourPoiPageItem(item);
    }

    private isCurrentTourPoiPageItem(item: SmartGuideMapItem): boolean {
        const selectedPoiId = this.currentContext?.selectedPoiId?.trim();
        return this.currentContext?.pageType === "tourPoi" &&
            this.isTourPoiMapItem(item) &&
            selectedPoiId?.toLowerCase() === item.id.trim().toLowerCase();
    }

    private renderTourRouteCard(route: DestinationTourRoute): void {
        const card = this.markerCardElement;
        const canOpenDetail = Boolean(route.canonicalUrl?.trim());
        card.dataset.smartguidePermanentMapMarkerType = "tour";
        card.dataset.smartguidePermanentMapSelectedId = route.id;
        this.markerTitleElement.textContent = route.name;

        const image = card.querySelector<HTMLImageElement>("[data-smart-guide-permanent-map-target='markerImage']");
        if (image) {
            image.src = route.imageUrl || "";
            image.hidden = !route.imageUrl;
        }

        const link = card.querySelector<HTMLAnchorElement>("[data-smart-guide-permanent-map-target='markerLink']");
        if (link) {
            if (canOpenDetail) {
                link.href = route.canonicalUrl;
                link.removeAttribute("aria-disabled");
            } else {
                link.removeAttribute("href");
                link.setAttribute("aria-disabled", "true");
            }
            link.hidden = false;
            link.setAttribute("aria-label", canOpenDetail ? formatTranslation(webTranslations.openItemFormat, route.name) : route.name);
        }

        const distance = card.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerDistance']");
        if (distance) {
            distance.hidden = true;
        }

        this.renderTourMeta(route);

        const chevron = card.querySelector<HTMLAnchorElement>("[data-smart-guide-permanent-map-target='markerChevron']");
        if (chevron) {
            chevron.hidden = !canOpenDetail;
            if (canOpenDetail) {
                chevron.href = route.canonicalUrl;
                chevron.removeAttribute("aria-disabled");
            } else {
                chevron.removeAttribute("href");
                chevron.setAttribute("aria-disabled", "true");
            }
            chevron.setAttribute("aria-label", canOpenDetail ? formatTranslation(webTranslations.openItemFormat, route.name) : route.name);
        }

        const audioButton = card.querySelector<HTMLButtonElement>("[data-smart-guide-permanent-map-target='markerAudioButton']");
        if (audioButton) {
            audioButton.hidden = true;
            audioButton.disabled = true;
        }

        const tourButton = card.querySelector<HTMLButtonElement>("[data-smart-guide-permanent-map-target='markerTourButton']");
        if (tourButton) {
            tourButton.hidden = true;
        }

        card.hidden = false;
    }

    private renderTourMeta(route: DestinationTourRoute): void {
        const meta = this.markerCardElement.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerTourMeta']");
        if (!meta) {
            return;
        }

        const length = this.formatTourLength(route.length);
        const duration = this.formatTourDuration(route.duration);
        const sightCount = this.formatTourSightCount(route.sightCount);
        this.setTourMetaItem("markerTourLength", length);
        this.setTourMetaItem("markerTourDuration", duration);
        this.setTourMetaItem("markerTourSightCount", sightCount);
        meta.hidden = !length && !duration && !sightCount;
    }

    private hideTourMeta(): void {
        const meta = this.markerCardElement.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerTourMeta']");
        if (meta) {
            meta.hidden = true;
        }
        this.setTourMetaItem("markerTourLength", "");
        this.setTourMetaItem("markerTourDuration", "");
        this.setTourMetaItem("markerTourSightCount", "");
    }

    private setTourMetaItem(targetName: string, text: string): void {
        const element = this.markerCardElement.querySelector<HTMLElement>(`[data-smart-guide-permanent-map-target='${targetName}']`);
        if (!element) {
            return;
        }

        const item = element.closest<HTMLElement>(".smartguide-permanent-map__marker-tour-meta-item");
        element.textContent = text;
        if (item) {
            item.hidden = !text;
        }
    }

    private formatTourLength(meters: number | null | undefined): string {
        const value = Number(meters);
        if (!Number.isFinite(value) || value <= 0) {
            return "";
        }

        if (value < 1000) {
            return `${Math.round(value)} m`;
        }

        return `${Math.round(value / 1000)} km`;
    }

    private formatTourDuration(durationMinutes: number | null | undefined): string {
        const value = Number(durationMinutes);
        if (!Number.isFinite(value) || value <= 0) {
            return "";
        }

        const minutes = Math.round(value);
        if (minutes <= 0) {
            return "";
        }

        const dayMinutes = 24 * 60;
        if (minutes >= dayMinutes) {
            const days = Math.floor(minutes / dayMinutes);
            return `${days} days ${minutes - days * dayMinutes} min`;
        }

        return `${minutes} min`;
    }

    private formatTourSightCount(sightCount: number | null | undefined): string {
        const value = Number(sightCount);
        return Number.isFinite(value) && value > 0 ? Math.round(value).toString() : "";
    }

    private playSelectedMarkerAudio(): void {
        if (!this.selectedItem) {
            return;
        }

        if (this.isLockedTourPoiMapItem(this.selectedItem)) {
            this.visitMarkerDetail(this.selectedItem);
            return;
        }

        const media = this.markerAudioMedia(this.selectedItem);
        if (!media) {
            return;
        }

        document.dispatchEvent(new CustomEvent<PersistentAudioMedia>(DomEvents.persistentAudioPlayRequested, {
            bubbles: true,
            detail: media
        }));
    }

    private markerAudioMedia(item: SmartGuideMapItem): PersistentAudioMedia | undefined {
        const url = item.audioUrl?.trim();
        if (!url) {
            return undefined;
        }

        return {
            id: item.audioId?.trim() || item.id,
            url,
            title: item.audioTitle?.trim() || item.title,
            artist: item.audioArtist?.trim() || "SmartGuide",
            artworkUrl: item.audioArtworkUrl?.trim() || item.imageUrl?.trim() || undefined,
            detailUrl: item.audioDetailUrl?.trim() || item.detailUrl?.trim() || window.location.pathname
        };
    }

    private visitMarkerDetail(item: SmartGuideMapItem): void {
        const url = item.detailUrl?.trim() || item.audioDetailUrl?.trim();
        if (!url) {
            return;
        }

        this.visit(url);
    }

    private visit(url: string): void {
        const turbo = (window as unknown as { Turbo?: { visit?: (url: string) => void } }).Turbo;
        if (typeof turbo?.visit === "function") {
            turbo.visit(url);
            return;
        }

        window.location.assign(url);
    }

    private formattedDistanceFromUser(item: SmartGuideMapItem): string | undefined {
        const userLocation = this.userLocationLngLat;
        if (!userLocation || !Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) {
            return undefined;
        }

        return this.formatKilometers(this.distanceInKilometers(userLocation, [item.longitude, item.latitude]));
    }

    private formatKilometers(distance: number): string | undefined {
        if (!Number.isFinite(distance)) {
            return undefined;
        }

        if (distance < 10) {
            return `${Math.max(0.1, distance).toFixed(1)} km`;
        }

        return `${Math.round(distance)} km`;
    }

    private distanceInKilometers(from: [number, number], to: [number, number]): number {
        const earthRadiusKilometers = 6371;
        const deltaLatitude = this.degreesToRadians(to[1] - from[1]);
        const deltaLongitude = this.degreesToRadians(to[0] - from[0]);
        const fromLatitude = this.degreesToRadians(from[1]);
        const toLatitude = this.degreesToRadians(to[1]);
        const haversine = Math.sin(deltaLatitude / 2) ** 2 +
            Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(deltaLongitude / 2) ** 2;

        return earthRadiusKilometers * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    }

    private refreshSelectedMarkerCard(): void {
        if (!this.selectedItem || this.markerCardElement.hidden) {
            return;
        }

        this.renderMarkerCard(this.selectedItem);
    }

    private requestCurrentLocation(
        centerOnNextUserLocation: boolean,
        options: { requestCompass?: boolean; centerZoom?: number } = {}
    ): void {
        if (centerOnNextUserLocation) {
            this.shouldCenterOnNextUserLocation = true;
            this.centerOnNextUserLocationZoom = options.centerZoom;
        }
        this.showLocationLoadingState();
        if (!this.unsubscribeMapLocation) {
            this.unsubscribeMapLocation = smartGuideLocationBroker.subscribe({
                id: "permanent-map",
                profile: "precise",
                onLocation: location => this.updateUserLocation(location),
                onError: error => this.handleUserLocationError(error)
            });
        }
        if (options.requestCompass !== false) {
            this.requestCompassHeading();
        }

        if (!this.map) {
            return;
        }

        this.renderUserLocationMarker();
        this.renderUserLocationAccuracy();
        this.centerOnUserLocationIfAvailable();
        this.renderCurrentLocationPointer();
        if (this.userLocationLngLat) {
            this.clearLocationUnavailable();
        }
    }

    private centerOnCurrentLocationWhenAvailable(): void {
        this.requestCompassHeading();

        if (this.userLocationSource === "gps" && this.userLocationLngLat) {
            this.followUserLocation = true;
            this.centerOnUserLocationIfAvailable();
            return;
        }

        if (this.userLocationSource === "ip" && this.userLocationLngLat) {
            this.shouldCenterOnNextUserLocation = true;
            this.centerOnUserLocationIfAvailable();
            return;
        }

        this.followUserLocation = true;
        this.centerOnUserLocationIfAvailable();
    }

    private cancelCurrentLocationFollow(): void {
        this.followUserLocation = false;
        this.shouldCenterOnNextUserLocation = false;
        this.centerOnNextUserLocationZoom = undefined;
    }

    private showLocationLoadingState(): void {
        this.statusElement.textContent = webTranslations.findingYourLocation;
    }

    private updateUserLocation(location: SmartGuideBrowserLocation): void {
        const latitude = Number(location.latitude);
        const longitude = Number(location.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return;
        }

        const lngLat: [number, number] = [longitude, latitude];
        this.userLocationLngLat = lngLat;
        this.userLocationSource = "gps";
        this.renderCurrentLocationFollowState();
        this.refreshSelectedMarkerCard();
        const accuracy = Number(location.accuracy);
        this.userLocationAccuracy = Number.isFinite(accuracy) && accuracy > 0 ? accuracy : undefined;
        if (this.userLocationHeadingSource !== "compass") {
            const heading = location.heading;
            this.userLocationHeading = typeof heading === "number" && Number.isFinite(heading)
                ? heading
                : undefined;
            this.userLocationHeadingSource = this.userLocationHeading === undefined ? undefined : "geolocation";
        }

        if (!this.map) {
            this.clearLocationUnavailable();
            return;
        }

        this.renderUserLocationMarker();
        this.renderUserLocationAccuracy();
        this.centerOnUserLocationIfAvailable();
        this.renderCurrentLocationPointer();
        this.clearLocationUnavailable();
    }

    private handleUserLocationError(error: GeolocationPositionError | Error): void {
        if (this.userLocationSource === "gps") {
            return;
        }

        if ("code" in error && error.code === geolocationPermissionDeniedCode) {
            this.followUserLocation = false;
            this.showLocationUnavailable(webTranslations.locationPermissionRequired);
            return;
        }

        void this.requestIpLocationFallback(webTranslations.currentLocationUnavailable, true);
    }

    private requestIpLocationFallback(errorMessage: string, showErrorOnFailure: boolean): Promise<void> {
        if (!this.ipLocationFallbackPromise) {
            this.ipLocationFallbackPromise = this.loadIpLocationFallback(errorMessage, showErrorOnFailure)
                .finally(() => {
                    this.ipLocationFallbackPromise = undefined;
                });
        }

        return this.ipLocationFallbackPromise;
    }

    private async loadIpLocationFallback(errorMessage: string, showErrorOnFailure: boolean): Promise<void> {
        const abortController = new AbortController();
        const timeoutId = window.setTimeout(() => abortController.abort(), userLocationIpFallbackTimeoutMs);

        try {
            const response = await fetch(userLocationIpFallbackUrl, {
                cache: "no-store",
                signal: abortController.signal
            });
            if (!response.ok) {
                throw new Error(`IP location request failed with HTTP ${response.status}.`);
            }

            const location = await response.json() as IpLocationResponse;
            const latitude = Number(location.latitude);
            const longitude = Number(location.longitude);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                throw new Error("IP location response did not include coordinates.");
            }

            if (this.userLocationSource === "gps") {
                return;
            }

            this.userLocationLngLat = [longitude, latitude];
            this.userLocationSource = "ip";
            this.followUserLocation = false;
            this.userLocationAccuracy = undefined;
            this.refreshSelectedMarkerCard();
            if (this.userLocationHeadingSource !== "compass") {
                this.userLocationHeading = undefined;
                this.userLocationHeadingSource = undefined;
            }

            this.renderUserLocationMarker();
            this.renderUserLocationAccuracy();
            this.centerOnUserLocationIfAvailable(false);
            this.renderCurrentLocationPointer();
            this.clearLocationUnavailable();
        } catch {
            if (!this.userLocationLngLat && showErrorOnFailure) {
                this.showLocationUnavailable(errorMessage);
            }
        } finally {
            window.clearTimeout(timeoutId);
        }
    }

    private centerOnUserLocationIfAvailable(consumeRequest = true): void {
        if (!this.map || !this.userLocationLngLat) {
            return;
        }

        if (this.followUserLocation && this.userLocationSource === "gps") {
            this.shouldCenterOnNextUserLocation = false;
            this.centerOnNextUserLocationZoom = undefined;
            this.map.easeTo?.({
                center: this.userLocationLngLat,
                duration: 350
            });
            this.hideCurrentLocationPointer();
            return;
        }

        if (!this.shouldCenterOnNextUserLocation) {
            return;
        }

        const requestedZoom = this.centerOnNextUserLocationZoom ?? 16;
        if (consumeRequest) {
            this.shouldCenterOnNextUserLocation = false;
            this.centerOnNextUserLocationZoom = undefined;
        }
        const currentZoom = Number(this.map.getZoom?.() ?? 0);
        this.map.easeTo?.({
            center: this.userLocationLngLat,
            zoom: Number.isFinite(currentZoom) ? Math.max(currentZoom, requestedZoom) : requestedZoom,
            duration: 350
        });
        this.hideCurrentLocationPointer();
    }

    private renderCurrentLocationFollowState(): void {
        const button = this.element.querySelector<HTMLElement>(
            "[data-smart-guide-permanent-map-current-location]"
        );
        if (!button) {
            return;
        }

        const isFollowingGps = this.followUserLocation &&
            this.userLocationSource === "gps" &&
            Boolean(this.userLocationLngLat);
        button.setAttribute("aria-pressed", isFollowingGps ? "true" : "false");
        button.querySelectorAll<HTMLElement>(
            "[data-smart-guide-permanent-map-current-location-icon]"
        ).forEach(icon => {
            icon.hidden = icon.dataset.smartGuidePermanentMapCurrentLocationIcon !==
                (isFollowingGps ? "active" : "inactive");
        });
    }

    private centerOnKnownUserLocation(): void {
        if (!this.map || !this.userLocationLngLat) {
            return;
        }

        const currentZoom = Number(this.map.getZoom?.() ?? 0);
        this.map.easeTo?.({
            center: this.userLocationLngLat,
            zoom: Number.isFinite(currentZoom) ? Math.max(currentZoom, 16) : 16,
            duration: 350
        });
        this.hideCurrentLocationPointer();
    }

    private renderUserLocationMarker(): void {
        if (!this.map || !this.mapModule || !this.userLocationLngLat) {
            return;
        }

        const markerElement = this.userLocationMarkerElement ?? this.createUserLocationMarkerElement();
        this.userLocationMarkerElement = markerElement;
        if (!this.userLocationMarker) {
            const marker = new this.mapModule.Marker({
                element: markerElement,
                anchor: "center"
            });
            marker.setLngLat(this.userLocationLngLat).addTo(this.map);
            this.userLocationMarker = marker;
        } else {
            this.userLocationMarker.setLngLat(this.userLocationLngLat);
        }

        this.updateUserLocationBearing();
    }

    private renderCurrentLocationPointer(): void {
        if (!this.map || !this.userLocationLngLat || !this.isVisible) {
            this.hideCurrentLocationPointer();
            return;
        }

        const bounds = this.readViewportBounds();
        if (!bounds || this.viewportContainsUserLocation(bounds, this.userLocationLngLat)) {
            this.hideCurrentLocationPointer();
            return;
        }

        const dimensions = this.currentMapDimensions();
        if (!dimensions) {
            this.hideCurrentLocationPointer();
            return;
        }

        const pointer = this.currentLocationPointerElement ?? this.createCurrentLocationPointerElement();
        this.currentLocationPointerElement = pointer;
        if (!pointer.parentElement) {
            this.element.append(pointer);
        }

        const iconSize = 40;
        const maxWidth = Math.max(0, dimensions.width - iconSize);
        const maxHeight = Math.max(0, dimensions.height - iconSize);
        const center = this.currentViewportCenter(bounds);
        const pointerRotation = this.rhumbBearing(center, this.userLocationLngLat);
        const mapRotation = Number(this.map.getBearing?.() ?? 0);
        const finalRotation = pointerRotation - (Number.isFinite(mapRotation) ? mapRotation : 0);
        const radius = Math.sqrt(maxHeight ** 2 + maxWidth ** 2) / 2;
        const left = maxWidth / 2 + radius * Math.sin(this.degreesToRadians(finalRotation));
        const bottom = maxHeight / 2 + radius * Math.cos(this.degreesToRadians(finalRotation));

        pointer.hidden = false;
        pointer.style.left = `${this.clamp(left, 0, maxWidth)}px`;
        pointer.style.bottom = `${this.clamp(bottom, 0, maxHeight)}px`;
        pointer.style.transform = `rotate(${finalRotation}deg)`;
    }

    private createCurrentLocationPointerElement(): HTMLElement {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "smartguide-permanent-map__current-location-pointer";
        button.dataset.smartGuidePermanentMapCurrentLocationPointer = "true";
        button.setAttribute("aria-label", webTranslations.currentLocation);
        button.hidden = true;

        const heading = document.createElement("span");
        heading.className = "smartguide-permanent-map__current-location-pointer-heading";

        const headingOutline = document.createElement("span");
        headingOutline.className = "smartguide-permanent-map__current-location-pointer-heading-outline";

        const headingCore = document.createElement("span");
        headingCore.className = "smartguide-permanent-map__current-location-pointer-heading-core";

        const outer = document.createElement("span");
        outer.className = "smartguide-permanent-map__current-location-pointer-outer";

        const middle = document.createElement("span");
        middle.className = "smartguide-permanent-map__current-location-pointer-middle";

        const dot = document.createElement("span");
        dot.className = "smartguide-permanent-map__current-location-pointer-dot";

        heading.append(headingOutline, headingCore);
        middle.append(dot);
        outer.append(middle);
        button.append(heading, outer);
        return button;
    }

    private hideCurrentLocationPointer(): void {
        if (this.currentLocationPointerElement) {
            this.currentLocationPointerElement.hidden = true;
        }
    }

    private createUserLocationMarkerElement(): HTMLElement {
        const marker = document.createElement("div");
        marker.className = "smartguide-permanent-map__user-location-marker";
        marker.dataset.smartguideUserLocationHasHeading = "false";

        const heading = document.createElement("span");
        heading.className = "smartguide-permanent-map__user-location-heading";

        const headingOutline = document.createElement("span");
        headingOutline.className = "smartguide-permanent-map__user-location-heading-outline";

        const headingCore = document.createElement("span");
        headingCore.className = "smartguide-permanent-map__user-location-heading-core";

        const outer = document.createElement("span");
        outer.className = "smartguide-permanent-map__user-location-outer";

        const middle = document.createElement("span");
        middle.className = "smartguide-permanent-map__user-location-middle";

        const dot = document.createElement("span");
        dot.className = "smartguide-permanent-map__user-location-dot";

        heading.append(headingOutline, headingCore);
        middle.append(dot);
        outer.append(middle);
        marker.append(heading, outer);
        return marker;
    }

    private renderUserLocationAccuracy(): void {
        if (!this.map || !this.userLocationLngLat) {
            return;
        }
        if (!this.styleLoaded && this.map.isStyleLoaded?.() === false) {
            return;
        }

        const featureCollection = this.buildUserLocationAccuracyFeatureCollection();
        const existingSource = this.map.getSource?.(userLocationSourceId);
        if (existingSource?.setData) {
            existingSource.setData(featureCollection);
        } else {
            this.map.addSource(userLocationSourceId, {
                type: "geojson",
                data: featureCollection
            });
        }

        this.ensureUserLocationAccuracyLayer();
    }

    private buildUserLocationAccuracyFeatureCollection(): any {
        const lngLat = this.userLocationLngLat;
        if (!lngLat || this.userLocationAccuracy === undefined) {
            return {
                type: "FeatureCollection",
                features: []
            };
        }

        const [longitude, latitude] = lngLat;
        return {
            type: "FeatureCollection",
            features: [
                {
                type: "Feature",
                geometry: this.buildAccuracyCircleGeometry(longitude, latitude, this.userLocationAccuracy),
                properties: {
                    kind: "accuracy"
                }
            }
            ]
        };
    }

    private buildAccuracyCircleGeometry(longitude: number, latitude: number, radiusMeters: number): any {
        const earthRadiusMeters = 6371008.8;
        const angularDistance = radiusMeters / earthRadiusMeters;
        const latitudeRadians = this.degreesToRadians(latitude);
        const longitudeRadians = this.degreesToRadians(longitude);
        const coordinates: number[][] = [];

        for (let index = 0; index <= 64; index += 1) {
            const bearing = this.degreesToRadians(index * 360 / 64);
            const circleLatitude = Math.asin(
                Math.sin(latitudeRadians) * Math.cos(angularDistance) +
                Math.cos(latitudeRadians) * Math.sin(angularDistance) * Math.cos(bearing)
            );
            const circleLongitude = longitudeRadians + Math.atan2(
                Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitudeRadians),
                Math.cos(angularDistance) - Math.sin(latitudeRadians) * Math.sin(circleLatitude)
            );

            coordinates.push([
                this.radiansToDegrees(circleLongitude),
                this.radiansToDegrees(circleLatitude)
            ]);
        }

        return {
            type: "Polygon",
            coordinates: [coordinates]
        };
    }

    private ensureUserLocationAccuracyLayer(): void {
        if (!this.map) {
            return;
        }

        if (!this.map.getLayer?.(userLocationAccuracyLayerId)) {
            this.map.addLayer({
                id: userLocationAccuracyLayerId,
                type: "fill",
                source: userLocationSourceId,
                filter: ["==", ["get", "kind"], "accuracy"],
                paint: {
                    "fill-color": userLocationBlue,
                    "fill-opacity": 0.2
                }
            });
        }
    }

    private updateUserLocationBearing(): void {
        const marker = this.userLocationMarkerElement;
        if (!marker) {
            return;
        }

        const heading = this.userLocationHeading;
        const hasHeading = typeof heading === "number" && Number.isFinite(heading);
        marker.dataset.smartguideUserLocationHasHeading = hasHeading ? "true" : "false";

        const headingElement = marker.querySelector<HTMLElement>(".smartguide-permanent-map__user-location-heading");
        if (!headingElement) {
            return;
        }

        if (!hasHeading) {
            headingElement.style.transform = "";
            return;
        }

        const mapBearing = Number(this.map?.getBearing?.() ?? 0);
        const normalizedMapBearing = Number.isFinite(mapBearing) ? mapBearing : 0;
        headingElement.style.transform = `rotate(${heading - normalizedMapBearing}deg)`;
    }

    private stopGeolocationWatch(): void {
        this.unsubscribeMapLocation?.();
        this.unsubscribeMapLocation = undefined;
    }

    private requestCompassHeading(): void {
        if (this.compassHeadingListening) {
            return;
        }

        const orientationEventConstructor = window.DeviceOrientationEvent as (typeof DeviceOrientationEvent & {
            requestPermission?: (absolute?: boolean) => Promise<PermissionState | "default">;
        }) | undefined;
        if (!orientationEventConstructor) {
            return;
        }

        if (typeof orientationEventConstructor.requestPermission === "function") {
            if (this.compassPermissionGranted) {
                this.startCompassHeadingUpdates();
                return;
            }

            if (this.compassPermissionRequestInFlight) {
                return;
            }

            this.compassPermissionRequestInFlight = true;
            this.startCompassHeadingUpdates();
            void orientationEventConstructor.requestPermission(true)
                .then(permissionState => {
                    this.compassPermissionGranted = permissionState === "granted";
                    if (!this.compassPermissionGranted || !this.isVisible) {
                        this.stopCompassHeadingUpdates();
                        return;
                    }

                    this.stopCompassHeadingUpdates();
                    this.startCompassHeadingUpdates();
                })
                .catch(() => {
                    this.stopCompassHeadingUpdates();
                })
                .finally(() => {
                    this.compassPermissionRequestInFlight = false;
                });
            return;
        }

        this.startCompassHeadingUpdates();
    }

    private startCompassHeadingUpdates(): void {
        if (this.compassHeadingListening) {
            return;
        }

        window.addEventListener("deviceorientationabsolute", this.onDeviceOrientation as EventListener, true);
        window.addEventListener("deviceorientation", this.onDeviceOrientation as EventListener, true);
        this.compassHeadingListening = true;
    }

    private stopCompassHeadingUpdates(): void {
        if (!this.compassHeadingListening) {
            return;
        }

        window.removeEventListener("deviceorientationabsolute", this.onDeviceOrientation as EventListener, true);
        window.removeEventListener("deviceorientation", this.onDeviceOrientation as EventListener, true);
        this.compassHeadingListening = false;
    }

    private readonly onDeviceOrientation = (event: DeviceOrientationEvent): void => {
        const heading = this.readCompassHeading(event);
        if (heading === undefined) {
            return;
        }

        this.userLocationHeading = heading;
        this.userLocationHeadingSource = "compass";
        this.updateUserLocationBearing();
    };

    private readCompassHeading(event: DeviceOrientationEvent): number | undefined {
        const webkitHeading = (event as DeviceOrientationEventWithWebkitCompass).webkitCompassHeading;
        if (typeof webkitHeading === "number" && Number.isFinite(webkitHeading)) {
            return this.normalizeHeading(webkitHeading);
        }

        const alpha = event.alpha;
        if (typeof alpha !== "number" || !Number.isFinite(alpha)) {
            return undefined;
        }

        if (event.type !== "deviceorientationabsolute" && event.absolute !== true) {
            return undefined;
        }

        return this.normalizeHeading(360 - alpha);
    }

    private normalizeHeading(heading: number): number {
        return ((heading % 360) + 360) % 360;
    }

    private degreesToRadians(value: number): number {
        return value * Math.PI / 180;
    }

    private radiansToDegrees(value: number): number {
        return value * 180 / Math.PI;
    }

    private refreshContext(): void {
        if (document.documentElement.hasAttribute("data-turbo-preview")) return;
        const contextElement = document.querySelector<HTMLScriptElement>("[data-smartguide-map-context]");
        if (!contextElement?.textContent?.trim()) {
            if (/^\/profile(?:\/|$)/.test(window.location.pathname) && this.currentContext) {
                this.updateChrome();
                return;
            }
            const homeContext = this.buildHomeMapContext();
            if (homeContext) {
                const wasHomeContext = this.currentContext?.pageType === "home";
                this.currentContext = this.currentContext?.pageType === "home"
                    ? {
                        ...this.currentContext,
                        itemsUrl: this.currentContext.itemsUrl || this.itemsUrl,
                        nativeAppUrl: this.currentContext.nativeAppUrl || this.nativeAppUrl || null
                    }
                    : homeContext;
                if (!wasHomeContext) {
                    this.clearSelectedMapFeature();
                }
                this.suppressedContextSelectedPoiId = undefined;
                this.updateChrome();
                return;
            }

            this.currentContext = undefined;
            this.suppressedContextSelectedPoiId = undefined;
            this.clearSelectedMapFeature();
            this.updateChrome();
            return;
        }

        try {
            const context = JSON.parse(contextElement.textContent) as SmartGuidePermanentMapContext;
            const previousContext = this.currentContext;
            const previousSelectedPoiId = this.currentContext?.selectedPoiId ?? null;
            const nextSelectedPoiId = context.selectedPoiId ?? null;
            const previousRouteKey = this.tourRoutesContextKey(this.currentContext);
            const nextRouteKey = this.tourRoutesContextKey(context);
            const previousPageType = this.currentContext?.pageType ?? null;
            const previousDestinationId = this.currentContext?.destinationId ?? null;
            this.currentContext = {
                ...context,
                itemsUrl: context.itemsUrl || this.itemsUrl,
                nativeAppUrl: context.nativeAppUrl || this.nativeAppUrl || null
            };
            if (context.tourContextKey) getTourPoiSelectionState(document)?.activateScope(context.tourContextKey);
            if (previousSelectedPoiId !== nextSelectedPoiId ||
                previousContext?.tourContextKey !== context.tourContextKey ||
                previousRouteKey !== nextRouteKey ||
                previousPageType !== context.pageType ||
                previousDestinationId !== (context.destinationId ?? null)) {
                const preserveSelectedTourPoi = this.shouldPreserveSelectedTourPoiMapFeature(previousContext, this.currentContext);
                if (!preserveSelectedTourPoi) {
                    this.clearSelectedMapFeature();
                    this.restoreSelectedTourPoiCameraOnNextTourShow = false;
                } else if (previousContext?.pageType === "tourPoi" && this.currentContext.pageType === "tour") {
                    this.restoreSelectedTourPoiCameraOnNextTourShow = true;
                }
                this.suppressedContextSelectedPoiId = undefined;
            }
            this.updateChrome();
        } catch {
            this.currentContext = undefined;
            this.suppressedContextSelectedPoiId = undefined;
            this.clearSelectedMapFeature();
            this.updateChrome();
        }
    }

    private shouldPreserveSelectedTourPoiMapFeature(
        previousContext: SmartGuidePermanentMapContext | undefined,
        nextContext: SmartGuidePermanentMapContext | undefined
    ): boolean {
        const selectedItem = this.selectedItem;
        if (!selectedItem || !this.isTourPoiMapItem(selectedItem)) {
            return false;
        }

        if (!previousContext || !nextContext) {
            return false;
        }

        const pageTypes = new Set([previousContext.pageType, nextContext.pageType]);
        if (!pageTypes.has("tour") || !pageTypes.has("tourPoi")) {
            return false;
        }

        if ((previousContext.destinationId ?? null) !== (nextContext.destinationId ?? null)) {
            return false;
        }

        if (this.tourMapContextKey(previousContext) !== this.tourMapContextKey(nextContext)) {
            return false;
        }

        const selectedItemId = selectedItem.id.trim().toLowerCase();
        const previousSelectedPoiId = previousContext.selectedPoiId?.trim().toLowerCase();
        const nextSelectedPoiId = nextContext.selectedPoiId?.trim().toLowerCase();
        return previousSelectedPoiId === selectedItemId || nextSelectedPoiId === selectedItemId;
    }

    private buildHomeMapContext(): SmartGuidePermanentMapContext | undefined {
        const homeElement = document.querySelector<HTMLElement>("[data-smartguide-home]");
        if (!homeElement && !/^\/profile(?:\/|$)/.test(window.location.pathname)) {
            return undefined;
        }

        return {
            pageType: "home",
            destinationId: null,
            language: uiLanguage,
            destinationName: "SmartGuide",
            itemsUrl: this.itemsUrl,
            nativeAppUrl: this.nativeAppUrl || null,
            isContentLoadedAutomatically: true,
            initialViewport: {
                kind: "currentLocation",
                radiusMeters: 0
            }
        };
    }

    private readViewportBounds(): ViewportBounds | undefined {
        const bounds = this.map?.getBounds?.();
        const west = Number(bounds?.getWest?.());
        const south = Number(bounds?.getSouth?.());
        const east = Number(bounds?.getEast?.());
        const north = Number(bounds?.getNorth?.());
        if (![west, south, east, north].every(Number.isFinite)) {
            return undefined;
        }

        return { west, south, east, north };
    }

    private viewportContainsUserLocation(bounds: ViewportBounds, lngLat: [number, number]): boolean {
        const [longitude, latitude] = lngLat;
        const latitudeInside = latitude >= bounds.south && latitude <= bounds.north;
        const longitudeInside = bounds.west <= bounds.east
            ? longitude >= bounds.west && longitude <= bounds.east
            : longitude >= bounds.west || longitude <= bounds.east;

        return latitudeInside && longitudeInside;
    }

    private currentViewportCenter(bounds: ViewportBounds): [number, number] {
        const longitude = bounds.west <= bounds.east
            ? (bounds.west + bounds.east) / 2
            : this.normalizeLongitude((bounds.west + bounds.east + 360) / 2);

        return [
            longitude,
            (bounds.south + bounds.north) / 2
        ];
    }

    private currentMapDimensions(): { width: number; height: number } | undefined {
        const rect = this.element.getBoundingClientRect();
        const width = Number(rect.width || this.element.clientWidth || this.canvasElement.clientWidth);
        const height = Number(rect.height || this.element.clientHeight || this.canvasElement.clientHeight);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            return undefined;
        }

        return { width, height };
    }

    private rhumbBearing(from: [number, number], to: [number, number]): number {
        const fromLatitude = this.degreesToRadians(from[1]);
        const toLatitude = this.degreesToRadians(to[1]);
        let deltaLongitude = this.degreesToRadians(to[0] - from[0]);
        const deltaPhi = Math.log(
            Math.tan(toLatitude / 2 + Math.PI / 4) /
            Math.tan(fromLatitude / 2 + Math.PI / 4)
        );

        if (Math.abs(deltaLongitude) > Math.PI) {
            deltaLongitude = deltaLongitude > 0
                ? -(2 * Math.PI - deltaLongitude)
                : 2 * Math.PI + deltaLongitude;
        }

        return this.normalizeHeading(this.radiansToDegrees(Math.atan2(deltaLongitude, deltaPhi)));
    }

    private normalizeLongitude(longitude: number): number {
        return ((longitude + 540) % 360) - 180;
    }

    private clamp(value: number, minimum: number, maximum: number): number {
        return Math.min(Math.max(value, minimum), maximum);
    }

    private updateChrome(): void {
        const isTourContext = this.currentContext?.pageType === "tour" || this.currentContext?.pageType === "tourPoi";
        const isHomeContext = this.currentContext?.pageType === "home";
        this.element.classList.toggle("smartguide-permanent-map--tour", isTourContext);
        this.element.classList.toggle("smartguide-permanent-map--home", isHomeContext);

        this.searchLabelElement.textContent = isHomeContext
            ? webTranslations.findDestinationsWorldwide
            : this.currentContext
            ? formatTranslation(webTranslations.searchInFormat, this.currentContext.destinationName || "SmartGuide")
            : formatTranslation(webTranslations.searchInFormat, "SmartGuide");

        const createTourLink = this.element.querySelector<HTMLAnchorElement>("[data-smart-guide-permanent-map-native-link]");
        if (createTourLink) {
            createTourLink.href = this.currentContext?.nativeAppUrl || this.nativeAppUrl || "#";
            createTourLink.hidden = this.currentContext?.hidePlanTour === true;
        }
    }

    private setMapNavActive(isMapActive: boolean): void {
        document.querySelectorAll<HTMLElement>("[data-smartguide-bottom-navigation-item]").forEach(item => {
            const key = item.dataset.smartguideBottomNavigationKey;
            const serverCurrent = item.dataset.smartguideBottomNavigationCurrent === "true";
            item.classList.toggle(
                "smartguide-bottom-navigation__item--active",
                isMapActive ? key === "map" : serverCurrent
            );
        });
    }

    private showUnavailable(message: string): void {
        this.element.dataset.smartguideMapUnavailable = "true";
        this.statusElement.textContent = message;
    }

    private showLocationUnavailable(message: string): void {
        this.locationStatusMessage = message;
        this.statusElement.textContent = message;
    }

    private clearMapUnavailable(): void {
        delete this.element.dataset.smartguideMapUnavailable;
        if (!this.locationStatusMessage) {
            this.statusElement.textContent = "";
        }
    }

    private clearLocationUnavailable(): void {
        this.locationStatusMessage = undefined;
        if (this.element.dataset.smartguideMapUnavailable === "true") {
            return;
        }

        this.statusElement.textContent = "";
    }

    private clearUnavailable(): void {
        delete this.element.dataset.smartguideMapUnavailable;
        this.statusElement.textContent = "";
    }

    private webGlIsAvailable(): boolean {
        try {
            const canvas = document.createElement("canvas");
            return Boolean(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
        } catch {
            return false;
        }
    }

    private get canvasElement(): HTMLElement {
        return this.element.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='canvas']") ?? this.element;
    }

    private get searchLabelElement(): HTMLElement {
        return this.element.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='searchLabel']") ?? this.element;
    }

    private get statusElement(): HTMLElement {
        return this.element.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='status']") ?? this.element;
    }

    private get markerCardElement(): HTMLElement {
        return this.element.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerCard']") ?? this.element;
    }

    private get markerTitleElement(): HTMLElement {
        return this.element.querySelector<HTMLElement>("[data-smart-guide-permanent-map-target='markerTitle']") ?? this.markerCardElement;
    }

    private get styleUrl(): string {
        return this.styleUrlValue || this.element.dataset.smartGuidePermanentMapStyleUrlValue || "https://tiles.openfreemap.org/styles/bright";
    }

    private get itemsUrl(): string {
        return this.itemsUrlValue || this.element.dataset.smartGuidePermanentMapItemsUrlValue || "/api/smartguide-map/items";
    }

    private get nativeAppUrl(): string {
        return this.element.dataset.smartGuidePermanentMapNativeAppUrlValue || "";
    }

    private get elementState(): PermanentMapElementState {
        let state = permanentMapElementStates.get(this.element);
        if (!state) {
            state = { styleLoaded: false };
            permanentMapElementStates.set(this.element, state);
        }

        return state;
    }

    private get map(): any | undefined {
        return this.elementState.map;
    }

    private set map(value: any | undefined) {
        this.elementState.map = value;
    }

    private get mapModule(): any | undefined {
        return this.elementState.mapModule;
    }

    private set mapModule(value: any | undefined) {
        this.elementState.mapModule = value;
    }

    private get selectedItem(): SmartGuideMapItem | undefined {
        return this.elementState.selectedItem;
    }

    private set selectedItem(value: SmartGuideMapItem | undefined) {
        this.elementState.selectedItem = value;
    }

    private get selectedTourRoute(): DestinationTourRoute | undefined {
        return this.elementState.selectedTourRoute;
    }

    private set selectedTourRoute(value: DestinationTourRoute | undefined) {
        this.elementState.selectedTourRoute = value;
    }

    private get styleLoaded(): boolean {
        return this.elementState.styleLoaded;
    }

    private set styleLoaded(value: boolean) {
        this.elementState.styleLoaded = value;
    }

    private get lastItems(): SmartGuideMapItem[] | undefined {
        return this.elementState.lastItems;
    }

    private set lastItems(value: SmartGuideMapItem[] | undefined) {
        this.elementState.lastItems = value;
    }

    private get photoMarkerDomMarkers(): Map<string, PhotoMarkerDomMarker> {
        this.elementState.photoMarkerDomMarkers ??= new Map<string, PhotoMarkerDomMarker>();
        return this.elementState.photoMarkerDomMarkers;
    }

    private get appliedContextKey(): string | undefined {
        return this.elementState.appliedContextKey;
    }

    private set appliedContextKey(value: string | undefined) {
        this.elementState.appliedContextKey = value;
    }

    private get pageScrollBeforeMap(): { left: number; top: number } | undefined {
        return this.elementState.pageScrollBeforeMap;
    }

    private set pageScrollBeforeMap(value: { left: number; top: number } | undefined) {
        this.elementState.pageScrollBeforeMap = value;
    }

    private get restoreSelectedTourPoiCameraOnNextTourShow(): boolean {
        return this.elementState.restoreSelectedTourPoiCameraOnNextTourShow === true;
    }

    private set restoreSelectedTourPoiCameraOnNextTourShow(value: boolean) {
        this.elementState.restoreSelectedTourPoiCameraOnNextTourShow = value;
    }

    private get localDestinationItemCaches(): Map<string, SmartGuideMapItem[]> {
        this.elementState.localDestinationItemCaches ??= new Map<string, SmartGuideMapItem[]>();
        return this.elementState.localDestinationItemCaches;
    }

    private get currentLocationPointerElement(): HTMLElement | undefined {
        return this.elementState.currentLocationPointerElement;
    }

    private set currentLocationPointerElement(value: HTMLElement | undefined) {
        this.elementState.currentLocationPointerElement = value;
    }

    private get userLocationMarker(): any | undefined {
        return this.elementState.userLocationMarker;
    }

    private set userLocationMarker(value: any | undefined) {
        this.elementState.userLocationMarker = value;
    }

    private get userLocationMarkerElement(): HTMLElement | undefined {
        return this.elementState.userLocationMarkerElement;
    }

    private set userLocationMarkerElement(value: HTMLElement | undefined) {
        this.elementState.userLocationMarkerElement = value;
    }

    private get userLocationHeading(): number | undefined {
        return this.elementState.userLocationHeading;
    }

    private set userLocationHeading(value: number | undefined) {
        this.elementState.userLocationHeading = value;
    }

    private get userLocationHeadingSource(): UserLocationHeadingSource | undefined {
        return this.elementState.userLocationHeadingSource;
    }

    private set userLocationHeadingSource(value: UserLocationHeadingSource | undefined) {
        this.elementState.userLocationHeadingSource = value;
    }

    private get userLocationSource(): UserLocationSource | undefined {
        return this.elementState.userLocationSource;
    }

    private set userLocationSource(value: UserLocationSource | undefined) {
        this.elementState.userLocationSource = value;
    }

    private get userLocationLngLat(): [number, number] | undefined {
        return this.elementState.userLocationLngLat;
    }

    private set userLocationLngLat(value: [number, number] | undefined) {
        this.elementState.userLocationLngLat = value;
    }

    private get userLocationAccuracy(): number | undefined {
        return this.elementState.userLocationAccuracy;
    }

    private set userLocationAccuracy(value: number | undefined) {
        this.elementState.userLocationAccuracy = value;
    }

    private get followUserLocation(): boolean {
        return this.elementState.followUserLocation === true;
    }

    private set followUserLocation(value: boolean) {
        this.elementState.followUserLocation = value;
        this.renderCurrentLocationFollowState();
    }

    private get isVisible(): boolean {
        return !this.element.hidden && document.body.classList.contains("smartguide-map-visible");
    }

    private tourRoutesContextKey(context: SmartGuidePermanentMapContext | undefined): string {
        return JSON.stringify((context?.tourRoutes ?? []).map(route => ({
            id: route.id,
            color: route.color,
            canonicalUrl: route.canonicalUrl,
            coordinates: route.routeCoordinates
        })));
    }

    private tourMapContextKey(context: SmartGuidePermanentMapContext | undefined): string {
        return JSON.stringify({
            routeCoordinates: context?.routeCoordinates ?? [],
            routePoiMarkers: (context?.routePoiMarkers ?? []).map(marker => ({
                id: marker.id,
                canonicalUrl: marker.canonicalUrl,
                latitude: marker.latitude,
                longitude: marker.longitude
            }))
        });
    }

    private get currentContextCameraKey(): string | undefined {
        if (!this.currentContext) {
            return undefined;
        }

        return JSON.stringify({
            destinationId: this.currentContext.destinationId ?? null,
            language: this.currentContext.language,
            pageType: this.currentContext.pageType,
            isContentLoadedAutomatically: this.currentContext.isContentLoadedAutomatically === true,
            selectedPoiId: this.currentContext.selectedPoiId ?? null,
            routeCoordinates: this.currentContext.routeCoordinates ?? null,
            routePoiMarkers: this.currentContext.routePoiMarkers ?? null,
            tourRoutes: this.currentContext.tourRoutes ?? null,
            initialViewport: this.currentContext.initialViewport
        });
    }

    private get localDestinationItemsCacheKey(): string | undefined {
        if (!this.currentContext || this.currentContext.pageType === "home" || this.currentContext.isContentLoadedAutomatically === true) {
            return undefined;
        }

        return JSON.stringify({
            destinationId: this.currentContext.destinationId ?? "",
            language: this.currentContext.language,
            labels: this.normalizedFilterValues(this.activeLabels),
            categories: this.normalizedFilterValues(this.activeCategories)
        });
    }

    private get activeLabels(): string[] {
        return this.activeFilterValues("labels");
    }

    private get activeCategories(): string[] {
        return this.activeFilterValues("categories");
    }

    private normalizedFilterValues(values: string[]): string[] {
        return values
            .map(value => value.trim().toLowerCase())
            .filter(value => value.length > 0)
            .sort((left, right) => left.localeCompare(right));
    }

    private activeFilterValues(kind: "labels" | "categories"): string[] {
        return Array.from(this.element.querySelectorAll<HTMLElement>(`[data-smart-guide-permanent-map-filter-${kind}][aria-pressed='true']`))
            .map(item => item.dataset[`smartGuidePermanentMapFilter${kind[0].toUpperCase()}${kind.slice(1)}`] || "")
            .filter(value => value.length > 0);
    }
}
